<template>
  <div class="delivery-order-details-wrapper">
    <!-- Start Breadcrumb -->
    <div class="mb-3">
      <Breadcrumb :items="items" />
    </div>
    <!-- End Breadcrumb -->

    <!-- Start Main Loader -->
    <MainLoader key="loader" v-if="loading" />
    <!-- End Main Loader -->

    <!-- ===== Start Showing Order Details ===== -->
    <div key="main" v-else-if="allData" class="main-order-content">
      <!-- Start Smart Scheduled Order Dispatch Cockpit -->
      <div
        v-if="isScheduledOrder && scheduleCountdown"
        class="scheduled-dispatch-cockpit mb-4"
        :class="[
          `period-${scheduledPeriodCode}`,
          `urgency-${scheduleCountdown.urgencyState}`,
        ]"
      >
        <!-- Top Cockpit Header: Period Day/Night Badge, Title, Live Status -->
        <div
          class="cockpit-top-bar d-flex flex-wrap align-items-center justify-content-between gap-3"
        >
          <div class="d-flex align-items-center gap-3">
            <div class="period-orb-icon">
              <i
                v-if="scheduledPeriodCode === 'am'"
                class="fal fa-sun-cloud"
              ></i>
              <i v-else class="fal fa-moon-stars"></i>
              <span class="orb-pulse-ring"></span>
            </div>
            <div>
              <div class="d-flex align-items-center gap-2 flex-wrap">
                <h5 class="cockpit-title mb-0 font-weight-bold">
                  طلب مجدول مسبقاً
                </h5>
                <span
                  class="period-badge-tag"
                  :class="`period-${scheduledPeriodCode}`"
                >
                  <i
                    :class="
                      scheduledPeriodCode === 'am'
                        ? 'fal fa-sun'
                        : 'fal fa-moon'
                    "
                    class="ml-1"
                  ></i>
                  فترة {{ scheduledPeriodLabel }}
                  <span
                    v-if="allData.take_preiod || allData.take_period"
                    class="badge-sub-code"
                  >
                    ({{
                      (
                        allData.take_preiod ||
                        allData.take_period ||
                        ""
                      ).toUpperCase()
                    }})
                  </span>
                </span>
                <span
                  class="urgency-pill-tag"
                  :class="`pill-${scheduleCountdown.urgencyState}`"
                >
                  <span class="pulse-dot"></span>
                  {{ scheduleCountdown.statusHeadline }}
                </span>
              </div>
            </div>
          </div>

          <!-- Live Digital Flight Clock Countdown (Only displayed when waiting for schedule time) -->
          <div
            class="cockpit-countdown-pod d-flex align-items-center gap-2"
            v-if="!scheduleCountdown.isPast && !scheduleCountdown.isCompleted"
          >
            <div class="pod-label-box text-right ml-2">
              <span class="d-block pod-sub">العد التنازلي المباشر</span>
              <span class="d-block pod-main font-weight-bold">
                المتبقي حتى الموعد
              </span>
            </div>

            <div class="pod-digits-group d-flex align-items-center">
              <div class="digit-box" v-if="scheduleCountdown.days > 0">
                <span class="digit-num">{{
                  padZero(scheduleCountdown.days)
                }}</span>
                <span class="digit-sub">يوم</span>
              </div>
              <span class="digit-divider" v-if="scheduleCountdown.days > 0"
                >:</span
              >

              <div class="digit-box">
                <span class="digit-num">{{
                  padZero(scheduleCountdown.hours)
                }}</span>
                <span class="digit-sub">ساعة</span>
              </div>
              <span class="digit-divider">:</span>

              <div class="digit-box">
                <span class="digit-num">{{
                  padZero(scheduleCountdown.minutes)
                }}</span>
                <span class="digit-sub">دقيقة</span>
              </div>
              <span class="digit-divider">:</span>

              <div class="digit-box digit-box-sec">
                <span class="digit-num">{{
                  padZero(scheduleCountdown.seconds)
                }}</span>
                <span class="digit-sub">ثانية</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Bottom Smart Operations Advice & Quick Chips -->
        <div
          class="cockpit-footer d-flex flex-wrap align-items-center justify-content-between gap-2 pt-3 mt-3 border-top-soft"
        >
          <div
            class="smart-advice-pill d-flex align-items-center gap-2"
            :class="`advice-${scheduleCountdown.urgencyState}`"
          >
            <i class="fal fa-sparkles"></i>
            <span>{{ scheduledSmartAdvice }}</span>
          </div>

          <div class="d-flex align-items-center gap-2 flex-wrap">
            <span class="cockpit-chip" v-if="allData.take_date">
              <i class="fal fa-calendar-alt ml-1"></i>
              تاريخ الجدولة: {{ allData.take_date }}
            </span>
            <span class="cockpit-chip" v-if="allData.take_time">
              <i class="fal fa-clock ml-1"></i>
              وقت الجدولة: {{ allData.take_time }} ({{ scheduledPeriodLabel }})
            </span>
            <span class="cockpit-chip" v-if="allData.fulfillment_type">
              <i class="fal fa-box ml-1"></i>
              {{
                allData.fulfillment_type == "delivery"
                  ? "توصيل"
                  : allData.fulfillment_type == "takeaway"
                  ? "استلام من المتجر"
                  : "في المتجر"
              }}
            </span>
          </div>
        </div>
      </div>
      <!-- End Smart Scheduled Order Dispatch Cockpit -->

      <!-- Top Modern Floating Header Bar -->
      <div class="order-hero-card mb-4">
        <div
          class="d-flex flex-wrap justify-content-between align-items-center gap-3"
        >
          <!-- Order ID & Status Summary -->
          <div class="d-flex align-items-center flex-wrap gap-3">
            <div class="order-id-badge" v-if="allData.order_number">
              <i class="far fa-receipt ml-2 text-primary"></i>
              <span>تفاصيل طلب: </span>
              <strong class="order-num">#{{ allData.order_number }}</strong>
            </div>

            <div class="status-badge-chip" v-if="allData.status_trans">
              <span class="pulse-indicator"></span>
              {{ allData.status_trans }}
            </div>

            <!-- Settled Status Chip -->
            <div class="settled-badge-chip" v-if="allData.is_settled">
              <i class="far fa-check-circle ml-1 text-success"></i>
              <span>تمت التسوية</span>
              <span
                v-if="allData.settlement_total"
                class="settlement-amount-tag mr-1"
                >({{ allData.settlement_total }})</span
              >
            </div>

            <div class="created-time-tag" v-if="allData.created_at">
              <i class="far fa-clock ml-1"></i>
              <span>{{ allData.created_at }}</span>
            </div>
          </div>

          <!-- Dynamic Action Buttons & Control Bar -->
          <div class="d-flex flex-wrap align-items-center gap-2">
            <!-- Settle and Finish Order Button -->
            <v-btn
              v-if="allData.can_settle_and_finish"
              @click.stop="settleDialog = true"
              :disabled="isSettleLoading"
              color="#0d9488"
              depressed
              class="custom-hero-btn text-white rounded-pill px-4"
            >
              <i class="far fa-hand-holding-usd ml-2"></i>
              تسوية وإنهاء
            </v-btn>

            <!-- Balance Transfer Button -->
            <v-btn
              depressed
              color="success"
              class="custom-hero-btn text-white rounded-pill px-4"
              :loading="balanceLoading"
              @click="isChangeBalanceModel = true"
            >
              <i class="far fa-wallet ml-2"></i>
              تحويل رصيد
            </v-btn>

            <!-- Assign Driver Autocomplete (Single Search & Select Input) -->
            <div
              class="select-driver-container"
              v-if="canAssignDriver && allData.status != 'admin_returned'"
            >
              <v-autocomplete
                v-model="selectedDriverId"
                label="تعيين سائق"
                placeholder="ابحث بالاسم أو اختر سائق..."
                solo
                dense
                hide-details
                clearable
                no-filter
                prepend-inner-icon="mdi-magnify"
                class="rounded-pill driver-select-field"
                :items="drivers"
                item-text="name"
                item-value="id"
                item-disabled="notEligible"
                no-data-text="لا يوجد سائقين مطابقين"
                :loading="driversLoading || isAssigningDriver"
                :search-input.sync="driverSearchText"
                @click="onDriverSelectClick"
                @focus="onDriverSelectClick"
                @change="assignDriver"
                @click:clear="clearDriverSearch"
              >
                <template v-slot:item="{ item }">
                  <div class="py-1">
                    <div>{{ item.name }}</div>
                    <div
                      v-if="item.wallet_category || item.notEligible"
                      class="small"
                      :class="item.notEligible ? 'text-danger' : 'text-muted'"
                    >
                      <span v-if="item.wallet_category"
                        >فئة المحفظة: {{ item.wallet_category_trans }}</span
                      >
                      <span v-if="item.notEligible">
                        - غير مؤهل لاستلام الطلب
                        <template v-if="item.recharge_message"
                          >({{ item.recharge_message }})</template
                        >
                      </span>
                    </div>
                  </div>
                </template>
              </v-autocomplete>
            </div>

            <!-- Finish Order Button -->
            <v-btn
              v-if="allData.status == 'driver_pre_finished'"
              @click="showPayType = true"
              color="success"
              depressed
              class="custom-hero-btn text-white rounded-pill px-4"
            >
              <i class="far fa-check-circle ml-2"></i>
              إنهاء الطلب
            </v-btn>

            <!-- Raise Invoice Button -->
            <v-btn
              v-if="
                allData.status == 'driver_pre_finished' &&
                !allData.resturant_zoz
              "
              @click="invoiceDialog = true"
              color="#783d73"
              depressed
              class="custom-hero-btn text-white rounded-pill px-4"
            >
              <i class="far fa-file-invoice-dollar ml-2"></i>
              رفع فاتورة
            </v-btn>

            <!-- Change Order Status Button -->
            <v-btn
              v-if="allData.next_available_status"
              @click="changeOrderStatus"
              :disabled="isChangeStatusLoading"
              color="#783d73"
              depressed
              class="custom-hero-btn text-white rounded-pill px-4"
            >
              <i class="far fa-exchange-alt ml-2"></i>
              {{
                isChangeStatusLoading
                  ? "جاري التحميل..."
                  : "تغيير الحالة إلى " +
                    getNextStatusLabel(allData.next_available_status)
              }}
            </v-btn>

            <!-- Return Order Button -->
            <v-btn
              v-if="allData.can_return"
              @click="openReturnDialog"
              color="#e65100"
              depressed
              class="custom-hero-btn text-white rounded-pill px-4"
            >
              <i class="far fa-undo-alt ml-2"></i>
              إرجاع الطلب
            </v-btn>

            <!-- Cancel Order Button -->
            <v-btn
              @click="cancelOrder"
              v-if="
                allData.status != 'driver_finished_delivery' &&
                allData.status != 'client_cancel' &&
                allData.status != 'admin_cancel' &&
                allData.status != 'restaurant_cancel' &&
                allData.status != 'restaurant_reject' &&
                allData.status != 'admin_returned'
              "
              color="error"
              depressed
              class="custom-hero-btn text-white rounded-pill px-4"
            >
              <i class="far fa-times-circle ml-2"></i>
              إلغاء الطلب
            </v-btn>
          </div>
        </div>
      </div>

      <!-- Quick Metrics Summary Grid -->
      <div class="row mb-4">
        <div class="col-6 col-md-3">
          <div class="kpi-metric-card">
            <div class="kpi-icon icon-purple">
              <i class="far fa-shopping-bag"></i>
            </div>
            <div class="kpi-info">
              <span class="kpi-label">طريقة الاستلام</span>
              <h6 class="kpi-value mb-0">
                <span v-if="allData.fulfillment_type == 'delivery'">توصيل</span>
                <span v-else-if="allData.fulfillment_type == 'takeaway'"
                  >استلام من المتجر</span
                >
                <span v-else-if="allData.fulfillment_type == 'dine_in'"
                  >في المتجر</span
                >
                <span v-else>-</span>
              </h6>
            </div>
          </div>
        </div>

        <div class="col-6 col-md-3">
          <div class="kpi-metric-card">
            <div class="kpi-icon icon-blue">
              <i class="far fa-map-marker-alt"></i>
            </div>
            <div class="kpi-info">
              <span class="kpi-label">الوقت والمسافة المتوقعة</span>
              <h6 class="kpi-value mb-0">
                {{ allData.expected_time || 0 }} دقيقة |
                {{ allData.expected_distance || 0 }} كم
              </h6>
            </div>
          </div>
        </div>

        <div class="col-6 col-md-3">
          <div class="kpi-metric-card">
            <div class="kpi-icon icon-green">
              <i class="far fa-money-bill-wave"></i>
            </div>
            <div class="kpi-info">
              <span class="kpi-label">الإجمالي المدفوع</span>
              <h6 class="kpi-value price-text mb-0">
                {{ allData.total_price || 0 }}
              </h6>
            </div>
          </div>
        </div>

        <div class="col-6 col-md-3">
          <div class="kpi-metric-card">
            <div class="kpi-icon icon-orange">
              <i class="far fa-credit-card"></i>
            </div>
            <div class="kpi-info">
              <span class="kpi-label">طريقة وحالة الدفع</span>
              <h6 class="kpi-value mb-0">
                {{ allData.payment_type == "cash" ? "نقدي" : "أون لاين" }}
                <span
                  v-if="allData.payment_type == 'online'"
                  class="badge-status-sm"
                  :class="allData.is_paid ? 'bg-success' : 'bg-warning'"
                >
                  ({{ allData.is_paid ? "مدفوع" : "غير مدفوع" }})
                </span>
              </h6>
            </div>
          </div>
        </div>
      </div>

      <!-- Horizontal Order Status Timeline -->
      <div
        class="modern-card mb-4 timeline-card-wrapper"
        v-if="
          allData &&
          allData.status_times &&
          Object.keys(allData.status_times).length
        "
      >
        <div
          class="card-header-sm d-flex justify-content-between align-items-center"
        >
          <span class="title"
            ><i class="far fa-stream ml-2"></i>توقيتات حالات الطلب الزمني</span
          >
          <span
            class="badge badge-pill badge-light-primary px-3 py-1 font-weight-bold"
          >
            {{ Object.keys(allData.status_times).length }} مراحل
          </span>
        </div>
        <div class="card-body-sm p-4 overflow-hidden">
          <div class="horizontal-timeline-scroll">
            <div class="horizontal-stepper">
              <div
                class="stepper-item"
                v-for="(time, key, idx) in allData.status_times"
                :key="'h-step-' + key"
              >
                <div
                  class="stepper-line"
                  v-if="idx < Object.keys(allData.status_times).length - 1"
                ></div>
                <div class="stepper-icon-circle">
                  <i class="far fa-check"></i>
                </div>
                <div class="stepper-details">
                  <span class="stepper-title">{{
                    getStatusTimeLabel(key)
                  }}</span>
                  <span class="stepper-time">{{ formatStatusTime(time) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Delivery Delay Timeline -->
      <div
        class="modern-card mb-4 timeline-card-wrapper"
        v-if="
          allData &&
          allData.delay_timeline &&
          allData.delay_timeline.steps &&
          allData.delay_timeline.steps.length
        "
      >
        <div
          class="card-header-sm d-flex justify-content-between align-items-center"
        >
          <span class="title"
            ><i class="far fa-hourglass-half ml-2"></i>حدود ومهل التأخير</span
          >
          <div class="d-flex align-items-center">
            <span
              class="badge badge-pill px-3 py-1 font-weight-bold"
              :class="
                allData.delay_timeline.total_delay_minutes > 0
                  ? 'badge-light-danger'
                  : 'badge-light-success'
              "
            >
              إجمالي التأخير {{ allData.delay_timeline.total_delay_minutes }}
              دقيقة ({{ allData.delay_timeline.delay_percentage }}%)
            </span>
            <v-tooltip bottom max-width="360">
              <template v-slot:activator="{ on, attrs }">
                <i
                  class="far fa-info-circle mx-2 text-muted cursor-pointer"
                  v-bind="attrs"
                  v-on="on"
                ></i>
              </template>
              <div class="text-center py-1 font-weight-medium">
                نسبة التأخير = (مجموع دقائق التأخير ÷ مجموع الدقائق المسموحة للمراحل التي بدأت) × 100
              </div>
            </v-tooltip>
          </div>
        </div>
        <div class="card-body-sm p-4 overflow-hidden">
          <div class="horizontal-timeline-scroll">
            <div class="horizontal-stepper">
              <div
                class="stepper-item"
                v-for="(step, idx) in allData.delay_timeline.steps"
                :key="'delay-step-' + (step.key || idx)"
              >
                <div
                  class="stepper-line"
                  v-if="idx < allData.delay_timeline.steps.length - 1"
                ></div>

                <!-- Support receive step: interactive button -->
                <template v-if="step.key === 'support_received'">
                  <button
                    type="button"
                    class="stepper-icon-circle stepper-icon-circle--btn"
                    :class="
                      step.button_color === 'red'
                        ? 'stepper-icon-circle--danger'
                        : 'stepper-icon-circle--success'
                    "
                    :disabled="step.is_received || isReceivingSupport"
                    @click="receiveBySupport(step)"
                  >
                    <i
                      v-if="isReceivingSupport"
                      class="far fa-spinner fa-spin"
                    ></i>
                    <i
                      v-else
                      :class="
                        step.is_received
                          ? 'far fa-check'
                          : 'far fa-headset'
                      "
                    ></i>
                  </button>
                  <div class="stepper-details">
                    <span class="stepper-title">{{
                      getDelayStepLabel(step.key)
                    }}</span>
                    <span class="stepper-time" v-if="step.is_received">
                      {{ step.support_employee_name }} -
                      {{ step.received_at_local || step.received_at }}
                    </span>
                    <span class="stepper-time" v-else>لم يتم الاستلام</span>
                    <span
                      v-if="step.allowed_minutes !== undefined"
                      class="stepper-time mt-1"
                      :class="{ 'stepper-time--late': isStepDelayed(step) }"
                      >{{ describeStep(step) }}</span
                    >
                  </div>
                </template>

                <!-- Delay percentage step -->
                <template v-else-if="step.key === 'order_delay_percentage'">
                  <div
                    class="stepper-icon-circle"
                    :class="
                      step.button_color === 'red'
                        ? 'stepper-icon-circle--danger'
                        : 'stepper-icon-circle--success'
                    "
                  >
                    <i
                      :class="
                        step.button_color === 'red'
                          ? 'far fa-exclamation-triangle'
                          : 'far fa-check'
                      "
                    ></i>
                  </div>
                  <div class="stepper-details">
                    <span class="stepper-title">{{
                      getDelayStepLabel(step.key)
                    }}</span>
                    <span class="stepper-time">{{ step.percentage }}%</span>
                  </div>
                </template>

                <!-- Default delay step -->
                <template v-else>
                  <div
                    class="stepper-icon-circle"
                    :class="
                      stepColor(step) === 'red'
                        ? 'stepper-icon-circle--danger'
                        : 'stepper-icon-circle--success'
                    "
                  >
                    <i
                      :class="
                        stepColor(step) === 'red'
                          ? 'far fa-exclamation-triangle'
                          : 'far fa-check'
                      "
                    ></i>
                  </div>
                  <div class="stepper-details">
                    <span class="stepper-title">{{
                      getDelayStepLabel(step.key)
                    }}</span>
                    <span class="stepper-time">{{ describeStep(step) }}</span>
                  </div>
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Main Layout Content (Left Sidebar + Right Details Area) -->
      <div class="row">
        <!-- Sidebar Column: Parties Information & Payment Card (col-lg-4) -->
        <div class="col-lg-4">
          <!-- Client Card -->
          <div class="modern-card party-card mb-4" v-if="allData.client">
            <div class="card-header-sm">
              <span class="title"
                ><i class="far fa-user ml-2"></i>بيانات العميل</span
              >
              <router-link
                :to="`/client/show/${allData.client.id}`"
                class="view-link"
              >
                عرض الملف <i class="far fa-arrow-left mr-1"></i>
              </router-link>
            </div>
            <div class="card-body-sm text-center">
              <div class="avatar-ring mb-2">
                <img :src="allData.client.avatar" alt="Client Avatar" />
              </div>
              <h6 class="font-weight-bold mb-1 card-name">
                {{ allData.client.full_name }}
              </h6>
              <div
                class="phone-chip dir-ltr"
                v-if="allData.client.phone_complete_form"
              >
                <i class="far fa-phone ml-2 icon-flip"></i>
                <span>{{ allData.client.phone_complete_form }}</span>
              </div>
              <div class="mt-3">
                <v-btn
                  small
                  depressed
                  :color="supportChatColor(allData.client)"
                  class="rounded-pill px-4 text-white"
                  @click="
                    openSupportChat(
                      'client',
                      allData.client,
                      allData.client.full_name,
                      allData.client.avatar
                    )
                  "
                >
                  <i class="far fa-comments ml-1"></i>
                  محادثة مع العميل
                </v-btn>
              </div>
            </div>
          </div>

          <!-- Driver Card -->
          <div class="modern-card party-card mb-4" v-if="allData.driver">
            <div class="card-header-sm">
              <span class="title"
                ><i class="far fa-steering-wheel ml-2"></i>بيانات السائق</span
              >
              <router-link
                :to="`/driver/show/${allData.driver.id}`"
                class="view-link"
              >
                عرض الملف <i class="far fa-arrow-left mr-1"></i>
              </router-link>
            </div>
            <div class="card-body-sm text-center">
              <div class="avatar-ring mb-2">
                <img :src="allData.driver.image" alt="Driver Avatar" />
              </div>
              <h6 class="font-weight-bold mb-1 card-name">
                {{ allData.driver.full_name }}
              </h6>
              <div
                class="d-flex justify-content-center align-items-center flex-wrap gap-2 mb-2"
              >
                <span class="badge badge-light-secondary">
                  النوع:
                  {{
                    allData.driver && allData.driver.gender
                      ? allData.driver.gender == "male"
                        ? "ذكر"
                        : "أنثى"
                      : allData.gender == "male"
                      ? "ذكر"
                      : "أنثى"
                  }}
                </span>
                <span class="badge badge-light-secondary">
                  عدد الطلبات الحالية :
                  {{
                    allData.driver.active_orders_count != null
                      ? allData.driver.active_orders_count
                      : 0
                  }}
                </span>
              </div>
              <div
                class="phone-chip dir-ltr mb-3"
                v-if="allData.driver.phone_complete_form"
              >
                <i class="far fa-phone ml-2 icon-flip"></i>
                <span>{{ allData.driver.phone_complete_form }}</span>
              </div>
              <div
                class="d-flex justify-content-center align-items-center flex-wrap gap-2"
              >
                <v-btn
                  small
                  depressed
                  :color="supportChatColor(allData.driver)"
                  class="rounded-pill px-4 text-white"
                  @click="
                    openSupportChat(
                      'driver',
                      allData.driver,
                      allData.driver.full_name,
                      allData.driver.image
                    )
                  "
                >
                  <i class="far fa-comments ml-1"></i>
                  محادثة مع السائق
                </v-btn>
                <v-btn
                  small
                  depressed
                  color="error"
                  class="rounded-pill px-4 text-white"
                  @click="driverFineDialog = true"
                >
                  <i class="far fa-exclamation-triangle ml-1"></i>
                  تطبيق مخالفة على السائق
                </v-btn>
              </div>
            </div>
          </div>

          <!-- Store Card (Google Restaurant) -->
          <div class="modern-card party-card mb-4" v-if="allData.resturant">
            <div class="card-header-sm">
              <span class="title"
                ><i class="fab fa-google ml-2 text-danger"></i>المتجر
                (جوجل)</span
              >
            </div>
            <div class="card-body-sm text-center">
              <div class="store-image-box mb-3">
                <img :src="allData.resturant.icon" alt="Store Icon" />
              </div>
              <h6 class="font-weight-bold mb-2 card-name">
                {{ allData.resturant.name }}
              </h6>
              <div
                class="store-location-badge mt-2"
                v-if="allData.resturant.location"
              >
                <i class="far fa-map-marker-alt text-danger ml-1"></i>
                <span class="location-text text-muted small">{{
                  allData.resturant.location
                }}</span>
              </div>
            </div>
          </div>

          <!-- Store Card (ZOZ Store) -->
          <div class="modern-card party-card mb-4" v-if="allData.resturant_zoz">
            <div
              class="card-header-sm d-flex justify-content-between align-items-center"
            >
              <span class="title"
                ><i class="far fa-store ml-2 text-primary"></i>المتجر</span
              >
              <router-link
                :to="`/store/show/${allData.resturant_zoz.id}`"
                class="view-link"
              >
                عرض المتجر <i class="far fa-arrow-left mr-1"></i>
              </router-link>
            </div>
            <div class="card-body-sm text-center">
              <div class="store-image-box mb-3">
                <img :src="allData.resturant_zoz.image" alt="Store Image" />
              </div>
              <h6 class="font-weight-bold mb-2 card-name">
                {{ allData.resturant_zoz.trad_name }}
              </h6>
              <div
                class="store-location-badge mt-2"
                v-if="allData.resturant_zoz.location"
              >
                <i class="far fa-map-marker-alt text-danger ml-1"></i>
                <span class="location-text text-muted small">{{
                  allData.resturant_zoz.location
                }}</span>
              </div>
              <div class="mt-3">
                <v-btn
                  small
                  depressed
                  :color="supportChatColor(allData.resturant_zoz)"
                  class="rounded-pill px-4 text-white"
                  @click="
                    openSupportChat(
                      'restaurant',
                      allData.resturant_zoz,
                      allData.resturant_zoz.trad_name,
                      allData.resturant_zoz.image
                    )
                  "
                >
                  <i class="far fa-comments ml-1"></i>
                  محادثة مع المتجر
                </v-btn>
              </div>
            </div>
          </div>

          <!-- Payment Financial Information Receipt Card -->
          <div class="modern-card receipt-card mb-4">
            <div class="card-header-sm">
              <span class="title"
                ><i class="far fa-file-invoice-dollar ml-2 text-primary"></i
                >تفاصيل الدفع والماليات</span
              >
            </div>
            <div class="card-body-sm">
              <div class="receipt-row">
                <span class="label"
                  ><i class="far fa-credit-card ml-2 text-primary"></i>نوع
                  الدفع</span
                >
                <span
                  class="payment-type-chip"
                  :class="
                    allData.payment_type == 'cash' ? 'chip-cash' : 'chip-online'
                  "
                >
                  {{ allData.payment_type == "cash" ? "نقدي" : "أون لاين" }}
                </span>
              </div>

              <div
                class="receipt-row"
                :class="allData.offer_amount && 'passive_value'"
              >
                <span class="label"
                  ><i class="far fa-truck-loading ml-2 text-info"></i>رسوم
                  التوصيل</span
                >
                <template
                  v-if="allData.has_coupon && allData.coupon_type == 'delivery'"
                >
                  <div class="d-flex align-items-center gap-2">
                    <span class="value font-weight-bold text-success">{{
                      allData.shiping_price_after_discount
                    }}</span>
                    <span class="strike-price text-muted">{{
                      allData.shiping
                    }}</span>
                  </div>
                </template>
                <span v-else class="value font-weight-bold">{{
                  allData.shiping
                }}</span>
              </div>

              <div class="receipt-row" v-if="allData.is_premium_shipping">
                <span class="label"
                  ><i class="far fa-tags ml-2 text-success"></i>سعر التوصيل بعد
                  الخصم</span
                >
                <span class="value font-weight-bold text-success">{{
                  allData.delivery_price_after_discount
                }}</span>
              </div>

              <div class="receipt-row" v-if="allData.offer_amount">
                <span class="label"
                  ><i class="far fa-badge-percent ml-2 text-success"></i>التوصيل
                  بعد العرض</span
                >
                <span class="value font-weight-bold text-success">{{
                  allData.offer_amount
                }}</span>
              </div>

              <div class="receipt-row" v-if="allData.service_fee">
                <span class="label"
                  ><i class="far fa-concierge-bell ml-2 text-warning"></i>ضريبة
                  الخدمة</span
                >
                <span class="value font-weight-bold">{{
                  allData.service_fee
                }}</span>
              </div>

              <div class="receipt-row">
                <span
                  class="label"
                  v-if="allData.sub_total_after_discount != null"
                >
                  <i class="far fa-shopping-basket ml-2 text-purple"></i>سعر
                  المنتجات بعد الخصم
                </span>
                <span class="label" v-else>
                  <i class="far fa-shopping-basket ml-2 text-purple"></i>سعر
                  المنتجات
                </span>
                <template>
                  <div
                    v-if="allData.sub_total_after_discount != null"
                    class="d-flex align-items-center gap-2"
                  >
                    <span class="value font-weight-bold text-success">{{
                      allData.sub_total_after_discount
                    }}</span>
                    <span class="strike-price text-muted">{{
                      allData.sub_total
                    }}</span>
                  </div>
                  <div v-else>
                    <span class="value font-weight-bold">{{
                      allData.sub_total
                    }}</span>
                  </div>
                </template>
              </div>

              <div class="receipt-row" v-if="allData.coupon_amount">
                <span class="label"
                  ><i class="far fa-ticket-alt ml-2 text-danger"></i>خصم
                  الكوبون</span
                >
                <span class="value text-danger font-weight-bold"
                  >- {{ allData.coupon_amount }}</span
                >
              </div>

              <!-- Coupon Details Block -->
              <div
                v-if="allData.coupon_code || allData.has_coupon"
                class="coupon-highlight-box my-2 p-2 rounded"
              >
                <div
                  class="d-flex justify-content-between align-items-center mb-1"
                >
                  <span class="small font-weight-bold text-primary"
                    ><i class="far fa-ticket-alt ml-1"></i>كوبون الخصم:</span
                  >
                  <span class="badge badge-success font-weight-bold">{{
                    allData.coupon_code
                  }}</span>
                </div>
                <div
                  class="d-flex justify-content-between align-items-center small"
                  v-if="allData.coupon_type"
                >
                  <span class="text-muted">نوع الخصم</span>
                  <span>
                    {{
                      allData.coupon_type == "meal"
                        ? "خصم على المنتجات"
                        : allData.coupon_type == "delivery"
                        ? "خصم على التوصيل"
                        : allData.coupon_type
                    }}
                  </span>
                </div>
              </div>

              <div class="receipt-row">
                <span class="label"
                  ><i class="far fa-percentage ml-2 text-secondary"></i>الضريبة
                  المضافة</span
                >
                <span class="value font-weight-bold">{{
                  allData.shiping_tax
                }}</span>
              </div>

              <!-- Highlighted Total Amount Box -->
              <div class="receipt-total-box my-3 p-3 rounded-lg">
                <span class="label font-weight-bold">اجمالي الطلب</span>
                <span class="total-amount">{{ allData.total_price }}</span>
              </div>

              <div
                class="receipt-row"
                v-if="allData.total_cost_on_client != null"
              >
                <span class="label"
                  ><i class="far fa-percentage ml-2 text-secondary"></i>اجمالي
                  المدفوع من قبل العميل</span
                >
                <span class="value font-weight-bold">{{
                  allData.total_cost_on_client
                }}</span>
              </div>

              <!-- Tax & Commission Breakdown -->
              <div class="commission-section pt-3 border-top">
                <div
                  class="receipt-row small mb-1"
                  v-if="allData.app_amount_price"
                >
                  <span class="text-muted"
                    ><i class="far fa-shield-check ml-1"></i>ضريبة التطبيق
                    (السائق {{ allData.app_amount }}%)</span
                  >
                  <span class="font-weight-bold text-dark">{{
                    allData.app_amount_price
                  }}</span>
                </div>
                <div
                  class="receipt-row small mb-1"
                  v-if="allData.restaurant_app_amount_price"
                >
                  <span class="text-muted"
                    ><i class="far fa-store ml-1"></i>ضريبة التطبيق (المتجر
                    {{ allData.restaurant_app_amount }}%)</span
                  >
                  <span class="font-weight-bold text-dark">{{
                    allData.restaurant_app_amount_price
                  }}</span>
                </div>
                <div
                  class="receipt-row small mb-1"
                  v-if="allData.company_driver_app_amount_price"
                >
                  <span class="text-muted"
                    ><i class="far fa-building ml-1"></i>ضريبة شركة المناديب ({{
                      allData.company_driver_app_amount
                    }}%)</span
                  >
                  <span class="font-weight-bold text-dark">{{
                    allData.company_driver_app_amount_price
                  }}</span>
                </div>
              </div>

              <!-- Return Financial Summary -->
              <div
                v-if="
                  allData.return_total != null && allData.return_total !== ''
                "
                class="return-financial-box mt-3 p-3 rounded-lg bg-light-danger border-danger"
              >
                <div
                  class="receipt-row"
                  v-if="
                    allData.return_distance_fee != null &&
                    allData.return_distance_fee !== ''
                  "
                >
                  <span class="label text-danger font-weight-bold"
                    ><i class="far fa-route ml-1"></i>رسوم مسافة الإرجاع</span
                  >
                  <span class="value text-danger font-weight-bold">{{
                    allData.return_distance_fee
                  }}</span>
                </div>
                <div class="receipt-row pt-2 border-top border-danger mt-2">
                  <span class="label font-weight-bold text-danger"
                    ><i class="far fa-undo-alt ml-1"></i>إجمالي الإرجاع</span
                  >
                  <span class="value font-weight-bold text-danger h6 mb-0">{{
                    allData.return_total
                  }}</span>
                </div>
              </div>

              <!-- Settlement Financial Summary -->
              <div
                v-if="allData.is_settled"
                class="settlement-financial-box mt-3 p-3 rounded-lg"
              >
                <div class="receipt-row font-weight-bold text-success">
                  <span class="label">
                    <i class="far fa-badge-check ml-1"></i> حالة التسوية
                  </span>
                  <span class="value">تمت التسوية</span>
                </div>
                <div
                  class="receipt-row mt-2"
                  v-if="
                    allData.settlement_total != null &&
                    allData.settlement_total !== ''
                  "
                >
                  <span class="label font-weight-bold">
                    <i class="far fa-hand-holding-usd ml-1 text-success"></i>
                    إجمالي التسوية
                  </span>
                  <span class="value font-weight-bold text-success h6 mb-0">
                    {{ allData.settlement_total }}
                  </span>
                </div>
                <div
                  class="receipt-row mt-2 small text-muted"
                  v-if="allData.settled_at"
                >
                  <span class="label">
                    <i class="far fa-clock ml-1"></i> تاريخ التسوية
                  </span>
                  <span class="value dir-ltr">
                    {{ allData.settled_at }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Document Images (License & Personal ID) -->
          <div class="modern-card mb-4" v-if="allData.license_image">
            <div class="card-header-sm">
              <span class="title"
                ><i class="far fa-id-card ml-2"></i>صورة الرخصة</span
              >
            </div>
            <div class="card-body-sm text-center">
              <div class="document-img-wrapper">
                <img
                  :src="allData.license_image"
                  @error="replaceImage"
                  alt="License Image"
                />
              </div>
            </div>
          </div>

          <div class="modern-card mb-4" v-if="allData.personal_id_image">
            <div class="card-header-sm">
              <span class="title"
                ><i class="far fa-user-badge ml-2"></i>صورة العميل</span
              >
            </div>
            <div class="card-body-sm text-center">
              <div class="document-img-wrapper">
                <img
                  :src="allData.personal_id_image"
                  @error="replaceImage"
                  alt="Personal ID Image"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Main Column: Map, Trajectory, Items, Logs, Notes (col-lg-8) -->
        <div class="col-lg-8">
          <!-- Live Route Map Container -->
          <div class="modern-card map-card mb-4">
            <div
              class="card-header-sm d-flex justify-content-between align-items-center"
            >
              <span class="title"
                ><i class="far fa-map-marked-alt ml-2"></i>تتبع الخريطة وخط سير
                الرحلة</span
              >
              <span
                class="badge badge-primary rounded-pill px-3"
                v-if="allData.driver"
              >
                <i class="far fa-location-arrow ml-1"></i>تتبع مباشر للسائق
              </span>
            </div>
            <div class="card-body-sm p-0 overflow-hidden rounded-bottom-lg">
              <GmapMap
                v-if="center"
                style="width: 100%; height: 500px"
                :center="center"
                :zoom="12"
                :draggable="true"
                map-type-id="terrain"
                :heading="90"
                :tilt="45"
                @click="handleMapClick"
              >
                <div>
                  <gmap-polyline
                    v-if="
                      allData &&
                      allData.expected_route &&
                      allData.expected_route.length > 0
                    "
                    :path="allData.expected_route"
                    :options="{
                      strokeColor: '#783d73',
                      strokeWeight: 5,
                      strokeOpacity: 0.8,
                    }"
                  >
                  </gmap-polyline>
                  <GmapMarker
                    v-if="
                      allData.expected_route &&
                      allData.expected_route.length > 0
                    "
                    :position="allData.expected_route[0]"
                    :icon="{
                      url: require('@/assets/images/restaurant.png'),
                      scaledSize: { width: 42, height: 42 },
                    }"
                  />
                  <GmapMarker
                    v-if="
                      allData.expected_route &&
                      allData.expected_route.length > 0
                    "
                    :position="
                      allData.expected_route[allData.expected_route.length - 1]
                    "
                    :icon="{
                      url: require('@/assets/images/client.png'),
                      scaledSize: { width: 42, height: 42 },
                    }"
                  />
                  <GmapMarker
                    v-if="marker && allData && allData.driver"
                    :icon="image"
                    :position="marker"
                    @click="goToPartnerPage(allData.driver.id)"
                  />
                </div>
              </GmapMap>
            </div>
          </div>

          <!-- Products Table Card -->
          <div class="modern-card mb-4" v-if="allData.items">
            <div
              class="card-header-sm d-flex justify-content-between align-items-center"
            >
              <span class="title"
                ><i class="far fa-utensils ml-2"></i>المنتجات المطلوبة</span
              >
              <span
                class="badge badge-light-primary rounded-pill px-3 font-weight-bold"
              >
                {{ allData.items.length }} منتجات
              </span>
            </div>
            <div class="card-body-sm p-0">
              <v-data-table
                class="modern-data-table"
                :headers="headers"
                :items="allData.items"
                :custom-filter="helper_filterSearch"
                :loading-text="$t('table.loadingData')"
                item-key="id"
                hide-default-footer
                :items-per-page="-1"
              >
                <template v-slot:no-data>
                  <div class="p-4 text-center text-muted">
                    {{ $t("table.noData") }}
                  </div>
                </template>

                <template v-slot:[`item.index`]="{ index }">
                  <span class="index-chip">{{ index + 1 }}</span>
                </template>

                <template v-slot:[`item.meal`]="{ item }">
                  <router-link
                    :to="`/products/show/${item.meal.id}`"
                    class="product-item-link"
                  >
                    <img :src="item.meal.image" class="product-thumb" alt="" />
                    <span class="product-name">{{ item.meal.name }}</span>
                  </router-link>
                </template>

                <template v-slot:[`item.quantity`]="{ item }">
                  <span
                    class="badge badge-pill badge-light-info px-3 py-1 font-weight-bold"
                  >
                    {{ item.quantity }}
                  </span>
                </template>

                <template v-slot:[`item.price`]="{ item }">
                  <span class="font-weight-bold text-dark">{{
                    item.price
                  }}</span>
                </template>

                <template v-slot:[`item.total_price`]="{ item }">
                  <span class="font-weight-bold main_color">{{
                    item.total_price
                  }}</span>
                </template>

                <template v-slot:[`item.actions`]="{ item }">
                  <div class="_actions">
                    <v-icon class="show" small @click="showItem(item)">
                      fal fa-eye
                    </v-icon>
                  </div>
                </template>
              </v-data-table>
            </div>
          </div>

          <!-- Customer Order Comment Card (Creative Design) -->
          <div
            class="modern-card order-comment-showcase-card mb-4"
            :class="hasOrderComment ? 'has-comment' : 'is-empty-comment'"
          >
            <div
              class="card-header-sm d-flex flex-wrap justify-content-between align-items-center gap-2"
            >
              <div class="d-flex align-items-center gap-2">
                <div class="comment-orb-icon">
                  <i class="far fa-comment-alt-dots"></i>
                  <span v-if="hasOrderComment" class="orb-ring"></span>
                </div>
                <div>
                  <span class="title d-block">تعليق وملاحظات العميل</span>
                  <span class="comment-subtitle text-muted">
                    تعليمات أو ملاحظات خاصة مرفقة مع الطلب
                  </span>
                </div>
              </div>

              <div class="d-flex align-items-center gap-2">
                <span
                  v-if="hasOrderComment"
                  class="comment-status-pill pill-active"
                >
                  <span class="status-dot"></span>
                  ملاحظة مرفقة
                </span>
                <span v-else class="comment-status-pill pill-muted">
                  لا يوجد تعليق
                </span>

                <button
                  v-if="hasOrderComment"
                  type="button"
                  class="copy-comment-btn"
                  :class="{ copied: commentCopied }"
                  @click="copyOrderComment"
                  title="نسخ التعليق"
                >
                  <i
                    :class="commentCopied ? 'far fa-check' : 'far fa-copy'"
                    class="ml-1"
                  ></i>
                  <span>{{ commentCopied ? "تم النسخ" : "نسخ" }}</span>
                </button>
              </div>
            </div>

            <div class="card-body-sm">
              <div v-if="hasOrderComment" class="comment-quote-stage">
                <i
                  class="fas fa-quote-left quote-watermark"
                  aria-hidden="true"
                ></i>

                <div class="comment-content-text">{{ allData.comment }}</div>

                <div
                  v-if="allData.client"
                  class="comment-author-bar d-flex align-items-center justify-content-between flex-wrap gap-2 mt-3 pt-3"
                >
                  <div class="d-flex align-items-center gap-2">
                    <img
                      v-if="allData.client.avatar"
                      :src="allData.client.avatar"
                      alt="Client Avatar"
                      class="author-mini-avatar"
                      @error="replaceImage"
                    />
                    <div>
                      <span class="author-name d-block font-weight-bold">
                        {{ allData.client.full_name }}
                      </span>
                      <span class="author-role text-muted">صاحب الطلب</span>
                    </div>
                  </div>

                  <span
                    v-if="allData.created_at"
                    class="comment-time-chip dir-ltr"
                  >
                    <i class="far fa-clock ml-1"></i>
                    {{ allData.created_at }}
                  </span>
                </div>
              </div>

              <div
                v-else
                class="comment-empty-box d-flex align-items-center gap-3"
              >
                <div class="empty-comment-icon">
                  <i class="far fa-comment-alt-slash"></i>
                </div>
                <div>
                  <h6 class="mb-1 font-weight-bold text-dark small">
                    لم يقم العميل بإضافة تعليق على هذا الطلب
                  </h6>
                  <p class="mb-0 text-muted extra-small">
                    لا توجد ملاحظات أو تعليمات إضافية مسجلة في حقل التعليق.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <!-- General Order Information Card -->
          <div class="modern-card mb-4">
            <div class="card-header-sm">
              <span class="title"
                ><i class="far fa-info-circle ml-2"></i>معلومات الطلب
                التفصيلية</span
              >
            </div>
            <div class="card-body-sm">
              <div class="info-grid-list">
                <div class="info-grid-item">
                  <span class="info-label">الحالة الحالية</span>
                  <span
                    class="info-value text-white badge bg-primary px-3 py-2 rounded-pill"
                    >{{ allData.status_trans }}</span
                  >
                </div>

                <div class="info-grid-item">
                  <span class="info-label">طريقة الاستلام</span>
                  <span class="info-value">
                    <v-chip
                      color="orange"
                      text-color="#fff"
                      small
                      v-if="allData.fulfillment_type == 'delivery'"
                      >توصيل</v-chip
                    >
                    <v-chip
                      color="orange"
                      text-color="#fff"
                      small
                      v-if="allData.fulfillment_type == 'takeaway'"
                      >استلام من المتجر</v-chip
                    >
                    <v-chip
                      color="orange"
                      text-color="#fff"
                      small
                      v-if="allData.fulfillment_type == 'dine_in'"
                      >في المتجر</v-chip
                    >
                  </span>
                </div>

                <div class="info-grid-item">
                  <span class="info-label">نوع السائق</span>
                  <span class="info-value">
                    <v-chip color="primary" text-color="#fff" small>{{
                      allData.gender
                        ? allData.gender == "male"
                          ? "ذكر"
                          : "انثى"
                        : "لا يوجد"
                    }}</v-chip>
                  </span>
                </div>

                <div class="info-grid-item">
                  <span class="info-label">المنصة</span>
                  <span class="info-value">
                    <v-chip
                      color="green"
                      text-color="#fff"
                      small
                      v-if="!allData.is_on_demand_rider"
                    >
                      {{
                        allData.platform == "mobile" ? "تطبيق" : "موقع إلكتروني"
                      }}
                    </v-chip>
                    <v-chip
                      color="blue"
                      text-color="#fff"
                      small
                      v-if="allData.is_on_demand_rider"
                    >
                      بحث عن سائق
                    </v-chip>
                  </span>
                </div>

                <div class="info-grid-item">
                  <span class="info-label">الوقت المتوقع</span>
                  <span class="info-value font-weight-bold"
                    >{{ allData.expected_time }} دقيقة</span
                  >
                </div>

                <div class="info-grid-item">
                  <span class="info-label">المسافة المتوقعة</span>
                  <span class="info-value font-weight-bold"
                    >{{ allData.expected_distance }} كم</span
                  >
                </div>

                <div
                  class="info-grid-item"
                  v-if="
                    allData.resturant_zoz &&
                    allData.fulfillment_type == 'dine_in'
                  "
                >
                  <span class="info-label">رقم الطاولة</span>
                  <span class="info-value font-weight-bold text-primary">{{
                    allData.order_text
                  }}</span>
                </div>

                <div class="info-grid-item">
                  <span class="info-label">تاريخ إنشاء الطلب</span>
                  <span class="info-value dir-ltr text-right">{{
                    allData.created_at
                  }}</span>
                </div>

                <div class="info-grid-item" v-if="allData.take_date">
                  <span class="info-label">تاريخ جدولة الطلب</span>
                  <span class="info-value dir-ltr text-right">
                    <span class="badge badge-pill badge-light-warning ml-1">
                      <i class="fal fa-calendar-alt"></i>
                    </span>
                    {{ allData.take_date }}
                  </span>
                </div>

                <div class="info-grid-item" v-if="allData.take_time">
                  <span class="info-label">وقت وفترة الجدولة</span>
                  <span class="info-value dir-ltr text-right">
                    <span class="badge badge-pill badge-light-warning ml-1">
                      <i
                        :class="
                          scheduledPeriodCode === 'am'
                            ? 'fal fa-sun'
                            : 'fal fa-moon'
                        "
                      ></i>
                    </span>
                    {{ allData.take_time }} ({{ scheduledPeriodLabel }})
                  </span>
                </div>

                <div class="info-grid-item" v-if="allData.is_on_demand_rider">
                  <span class="info-label">تكلفة التوصيل على</span>
                  <span class="info-value">
                    <v-chip color="orange" text-color="#fff" small>
                      {{ deliveryCostOnLabel }}
                    </v-chip>
                  </span>
                </div>

                <div
                  class="info-grid-item"
                  v-if="
                    allData.is_on_demand_rider &&
                    allData.delivery_cost_on == 'restaurant'
                  "
                >
                  <span class="info-label">إجمالي التكلفة على المطعم</span>
                  <span class="info-value font-weight-bold text-danger">{{
                    allData.total_cost_on_restaurant
                  }}</span>
                </div>

                <div
                  class="info-grid-item"
                  v-if="allData.total_cost_on_client != null"
                >
                  <span class="info-label">إجمالي التكلفة على العميل</span>
                  <span class="info-value font-weight-bold text-primary">{{
                    allData.total_cost_on_client
                  }}</span>
                </div>

                <div class="info-grid-item">
                  <span class="info-label">سعر الشحن بعد الخصم</span>
                  <span class="info-value">{{
                    allData.shiping_price_after_discount || "لا يوجد"
                  }}</span>
                </div>

                <div class="info-grid-item">
                  <span class="info-label">المجموع الفرعي بعد الخصم</span>
                  <span class="info-value">{{
                    allData.sub_total_after_discount || "لا يوجد"
                  }}</span>
                </div>

                <div
                  class="info-grid-item"
                  v-if="allData.payment_type == 'online'"
                >
                  <span class="info-label">حالة الدفع الإلكتروني</span>
                  <span
                    class="info-value"
                    :class="allData.is_paid ? 'text-success' : 'text-danger'"
                  >
                    {{ allData.is_paid ? "مدفوع" : "غير مدفوع" }}
                  </span>
                </div>

                <div class="info-grid-item">
                  <span class="info-label">طريقة الدفع</span>
                  <span class="info-value font-weight-bold">{{
                    allData.payment_type
                  }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Reason of Finished Card -->
          <div
            class="modern-card mb-4 border-left-success"
            v-if="allData.reason_of_finished"
          >
            <div class="card-header-sm">
              <span class="title text-success"
                ><i class="far fa-check-circle ml-2"></i>سبب الانهاء</span
              >
            </div>
            <div class="card-body-sm">
              <p class="mb-0 text-dark font-weight-bold">
                {{ allData.reason_of_finished }}
              </p>
            </div>
          </div>

          <!-- Reason of Rejection/Cancellation Card -->
          <div
            class="modern-card mb-4 border-left-danger"
            v-if="allData.cancel_reason"
          >
            <div class="card-header-sm">
              <span class="title text-danger"
                ><i class="far fa-times-circle ml-2"></i>سبب الإلغاء /
                الرفض</span
              >
            </div>
            <div class="card-body-sm">
              <h6 class="font-weight-bold text-danger mb-2">
                {{ allData.cancel_reason.name }}
              </h6>
              <div v-if="allData.cancel_reason_text">
                <span class="text-muted small">تعليق العميل:</span>
                <p class="mb-0 text-dark bg-light p-2 rounded mt-1">
                  {{ allData.cancel_reason_text }}
                </p>
              </div>
            </div>
          </div>

          <!-- Return Details Card -->
          <div
            class="modern-card mb-4 return-details-card"
            v-if="
              (allData.return_other_text != null &&
                allData.return_other_text !== '') ||
              (allData.return_distance_fee != null &&
                allData.return_distance_fee !== '') ||
              (allData.return_total != null && allData.return_total !== '')
            "
          >
            <div
              class="card-header-sm d-flex justify-content-between align-items-center"
            >
              <span class="title text-warning font-weight-bold">
                <i class="far fa-undo-alt ml-2"></i>تفاصيل إرجاع الطلب
              </span>
              <span
                class="badge badge-warning text-white rounded-pill px-3 py-1 font-weight-bold"
              >
                إرجاع
              </span>
            </div>
            <div class="card-body-sm">
              <div
                class="return-detail-row"
                v-if="
                  allData.return_distance_fee != null &&
                  allData.return_distance_fee !== ''
                "
              >
                <span class="return-detail-label">
                  <i class="far fa-route ml-2 text-warning"></i>رسوم مسافة
                  الإرجاع
                </span>
                <span class="return-detail-value font-weight-bold">{{
                  allData.return_distance_fee
                }}</span>
              </div>

              <div
                class="return-detail-row"
                v-if="
                  allData.return_other_text != null &&
                  allData.return_other_text !== ''
                "
              >
                <span class="return-detail-label">
                  <i class="far fa-comment-alt-text ml-2 text-info"></i>ملاحظات
                  الإرجاع
                </span>
                <span class="return-detail-value font-weight-bold">{{
                  allData.return_other_text
                }}</span>
              </div>

              <div
                class="return-total-highlight-row"
                v-if="
                  allData.return_total != null && allData.return_total !== ''
                "
              >
                <span class="return-total-label font-weight-bold">
                  <i class="far fa-coins ml-2"></i>إجمالي الإرجاع
                </span>
                <span class="return-total-value font-weight-bold">{{
                  allData.return_total
                }}</span>
              </div>
            </div>
          </div>

          <!-- Admin Notes Card -->
          <div class="modern-card mb-4">
            <div class="card-header-sm">
              <span class="title"
                ><i class="far fa-comment-alt-lines ml-2"></i>ملاحظات
                الإدارة</span
              >
            </div>
            <div class="card-body-sm">
              <div class="form-group mb-3">
                <textarea
                  class="form-control custom-admin-textarea"
                  v-model="feedbackText"
                  rows="3"
                  placeholder="أدخل ملاحظات الإدارة هنا..."
                ></textarea>
              </div>
              <div class="text-left">
                <v-btn
                  depressed
                  color="#783d73"
                  class="text-white rounded-pill px-6"
                  :loading="isSubmittingFeedback"
                  @click="submitFeedback"
                >
                  <i class="far fa-save ml-2"></i>
                  حفظ الملاحظات
                </v-btn>
              </div>
            </div>
          </div>

          <!-- Driver Candidates Table -->
          <div class="modern-card mb-4" v-if="orderCandidates.length">
            <div class="card-header-sm">
              <span class="title"
                ><i class="far fa-users ml-2"></i>المرشحون للطلب
                <span class="badge badge-pill badge-light-warning ml-1">{{
                  orderCandidates.length
                }}</span></span
              >
            </div>
            <div class="p-0">
              <order-candidates
                :candidates="orderCandidates"
                :order-id="id"
                order-type="delivery"
                :search-started-at="allData.driver_search_started_at"
              />
            </div>
          </div>

          <!-- 3 Combined Tables Section with Tabs Navigation -->
          <div class="modern-card mb-4 tabs-section-card">
            <!-- Tabs Navigation Bar -->
            <div class="tabs-header-bar p-2 border-bottom bg-light">
              <div class="nav nav-pills nav-justified gap-2 custom-nav-pills">
                <button
                  type="button"
                  class="nav-link custom-tab-btn"
                  :class="{ active: activeLogTab === 'wallet' }"
                  @click="activeLogTab = 'wallet'"
                  v-if="allData.wallet_transactions"
                >
                  <i class="far fa-wallet ml-2"></i>
                  عمليات المحفظة
                  <span
                    class="badge badge-pill badge-light-primary ml-1"
                    v-if="allData.wallet_transactions"
                  >
                    {{ allData.wallet_transactions.length }}
                  </span>
                </button>

                <button
                  type="button"
                  class="nav-link custom-tab-btn"
                  :class="{ active: activeLogTab === 'logs' }"
                  @click="activeLogTab = 'logs'"
                  v-if="allData.order_logs"
                >
                  <i class="far fa-history ml-2"></i>
                  العمليات على الطلب
                  <span
                    class="badge badge-pill badge-light-info ml-1"
                    v-if="allData.order_logs"
                  >
                    {{ allData.order_logs.length }}
                  </span>
                </button>

                <button
                  type="button"
                  class="nav-link custom-tab-btn"
                  :class="{ active: activeLogTab === 'calls' }"
                  @click="activeLogTab = 'calls'"
                  v-if="allData.call_logs && allData.call_logs.length"
                >
                  <i class="far fa-phone-alt ml-2"></i>
                  سجل المكالمات والواتساب
                  <span
                    class="badge badge-pill badge-light-warning ml-1"
                    v-if="allData.call_logs"
                  >
                    {{ allData.call_logs.length }}
                  </span>
                </button>
              </div>
            </div>

            <!-- Tab Content 1: Wallet Transactions Table -->
            <div
              class="card-body-sm p-0"
              v-if="activeLogTab === 'wallet' && allData.wallet_transactions"
            >
              <v-data-table
                class="modern-data-table wallet-transactions-table"
                :headers="headers2"
                :items="allData.wallet_transactions"
                :custom-filter="helper_filterSearch"
                :loading-text="$t('table.loadingData')"
                item-key="id"
                hide-default-footer
                :items-per-page="-1"
                :item-class="getWalletRowClass"
              >
                <template v-slot:no-data>
                  <div class="p-4 text-center text-muted">
                    {{ $t("table.noData") }}
                  </div>
                </template>

                <template v-slot:[`item.index`]="{ index }">
                  <span class="index-chip">{{ index + 1 }}</span>
                </template>

                <template v-slot:[`item.meal`]="{ item }">
                  <router-link
                    :to="`/products/show/${item.meal.id}`"
                    class="product-item-link"
                  >
                    <img :src="item.meal.image" class="product-thumb" alt="" />
                    <span>{{ item.meal.name }}</span>
                  </router-link>
                </template>

                <template v-slot:[`item.actions`]="{ item }">
                  <div class="_actions">
                    <v-icon class="show" small @click="showItem(item)">
                      fal fa-eye
                    </v-icon>
                  </div>
                </template>
              </v-data-table>
            </div>

            <!-- Tab Content 2: Order Logs Table -->
            <div
              class="card-body-sm p-0"
              v-if="activeLogTab === 'logs' && allData.order_logs"
            >
              <v-data-table
                class="modern-data-table"
                :headers="headers3"
                :items="allData.order_logs"
                :loading-text="$t('table.loadingData')"
                item-key="id"
                hide-default-footer
                :items-per-page="-1"
              >
                <template v-slot:no-data>
                  <div class="p-4 text-center text-muted">
                    {{ $t("table.noData") }}
                  </div>
                </template>

                <template v-slot:[`item.index`]="{ index }">
                  <span class="index-chip">{{ index + 1 }}</span>
                </template>

                <template v-slot:[`item.type`]="{ item }">
                  <span
                    v-if="item.type == 'deposit'"
                    class="badge badge-light-success px-3 py-1 font-weight-bold"
                    >إيداع</span
                  >
                  <span
                    v-if="item.type == 'to_withdraw'"
                    class="badge badge-light-danger px-3 py-1 font-weight-bold"
                    >سحب</span
                  >
                </template>

                <template v-slot:[`item.actions`]="{ item }">
                  <div class="_actions">
                    <v-icon class="show" small @click="showItem(item)">
                      fal fa-eye
                    </v-icon>
                  </div>
                </template>
              </v-data-table>
            </div>

            <!-- Tab Content 3: Call Logs Table -->
            <div
              class="card-body-sm p-0"
              v-if="
                activeLogTab === 'calls' &&
                allData.call_logs &&
                allData.call_logs.length
              "
            >
              <v-data-table
                class="modern-data-table"
                :headers="headers4"
                :items="allData.call_logs"
                :loading-text="$t('table.loadingData')"
                item-key="id"
                hide-default-footer
                :items-per-page="-1"
              >
                <template v-slot:no-data>
                  <div class="p-4 text-center text-muted">
                    {{ $t("table.noData") }}
                  </div>
                </template>

                <template v-slot:[`item.index`]="{ index }">
                  <span class="index-chip">{{ index + 1 }}</span>
                </template>

                <template v-slot:[`item.caller`]="{ item }">
                  <div
                    class="d-flex align-items-center justify-content-center gap-2"
                  >
                    <div class="text-right">
                      <div class="font-weight-bold">
                        {{ item.caller.full_name }}
                      </div>
                      <div class="small text-muted dir-ltr">
                        +{{ item.caller.phone_code }}{{ item.caller.phone }}
                      </div>
                    </div>
                  </div>
                </template>

                <template v-slot:[`item.receiver`]="{ item }">
                  <div class="text-center">
                    <div class="font-weight-bold">
                      {{
                        item.receiver && item.receiver.full_name
                          ? item.receiver.full_name
                          : "-"
                      }}
                    </div>
                    <div
                      class="small text-muted dir-ltr"
                      v-if="
                        item.receiver_phone_complete_form || item.receiver_phone
                      "
                    >
                      +{{
                        item.receiver_phone_complete_form ||
                        `${item.receiver_phone_code || ""}${
                          item.receiver_phone
                        }`
                      }}
                    </div>
                    <div class="small text-muted" v-if="item.receiver_type">
                      {{ userTypeLabel(item.receiver_type) }}
                    </div>
                  </div>
                </template>

                <template v-slot:[`item.action_type`]="{ item }">
                  <v-chip
                    small
                    :color="callLogActionColor(item.action_type)"
                    text-color="white"
                    class="font-weight-bold"
                  >
                    <i
                      :class="callLogActionIcon(item.action_type)"
                      class="ml-1"
                    ></i>
                    {{ callLogActionLabel(item.action_type) }}
                  </v-chip>
                </template>

                <template v-slot:[`item.message_body`]="{ item }">
                  <div v-if="item.message_body" class="call-log-message">
                    {{ item.message_body }}
                  </div>
                  <span v-else class="text-muted">-</span>
                </template>

                <template v-slot:[`item.caller.user_type`]="{ item }">
                  <v-chip
                    small
                    :color="
                      item.caller.user_type === 'client'
                        ? 'blue'
                        : item.caller.user_type === 'driver'
                        ? 'green'
                        : 'orange'
                    "
                    text-color="white"
                    class="font-weight-bold"
                  >
                    {{
                      item.caller.user_type === "client"
                        ? "عميل"
                        : item.caller.user_type === "driver"
                        ? "سائق"
                        : item.caller.user_type
                    }}
                  </v-chip>
                </template>
              </v-data-table>
            </div>
          </div>
        </div>
      </div>

      <!-- Chat Component Section -->
      <div class="row mt-4">
        <div class="col-12">
          <div class="modern-card chat-section-card">
            <div
              class="card-header-sm d-flex justify-content-between align-items-center"
            >
              <span class="title"
                ><i class="far fa-comments ml-2"></i>محادثات الطلب</span
              >
              <span
                class="badge badge-pill font-weight-bold"
                :class="
                  orderChats.length ? 'badge-primary' : 'badge-light-secondary'
                "
              >
                {{
                  orderChats.length
                    ? orderChats.length + " محادثة"
                    : "لا توجد محادثات"
                }}
              </span>
            </div>

            <div class="card-body-sm p-3">
              <!-- Chats Preview (grouped by conversation type) -->
              <template v-if="orderChats.length">
                <!-- Conversation Types Tabs -->
                <div class="chat-tabs-bar mb-3" v-if="orderChats.length > 1">
                  <button
                    v-for="(chat, chatIndex) in orderChats"
                    :key="chat.key"
                    type="button"
                    class="chat-tab-btn"
                    :class="{ active: activeChatIndex === chatIndex }"
                    @click="activeChatIndex = chatIndex"
                  >
                    <i class="far fa-comments ml-1"></i>
                    {{ conversationTypeLabel(chat.conversation_type) }}
                    <span
                      class="badge badge-pill badge-light-primary mr-1"
                      v-if="chat.messages_count"
                    >
                      {{ chat.messages_count }}
                    </span>
                  </button>
                </div>

                <div v-if="activeChat" class="chat-pane">
                  <!-- Chat Participants -->
                  <div class="chat-pane-header">
                    <div class="chat-participant" v-if="activeChat.sender">
                      <img
                        :src="userImage(activeChat.sender)"
                        class="chat-participant-img"
                        alt=""
                      />
                      <div class="text-right">
                        <div class="font-weight-bold">
                          {{ activeChat.sender.full_name || "-" }}
                        </div>
                        <div class="small text-muted">
                          {{ userTypeLabel(activeChat.sender.user_type) }}
                        </div>
                      </div>
                    </div>

                    <i class="far fa-exchange-alt chat-participant-sep"></i>

                    <div class="chat-participant" v-if="activeChat.receiver">
                      <img
                        :src="userImage(activeChat.receiver)"
                        class="chat-participant-img"
                        alt=""
                      />
                      <div class="text-right">
                        <div class="font-weight-bold">
                          {{ activeChat.receiver.full_name || "-" }}
                        </div>
                        <div class="small text-muted">
                          {{ userTypeLabel(activeChat.receiver.user_type) }}
                        </div>
                      </div>
                    </div>

                    <span class="chat-type-chip">
                      {{ conversationTypeLabel(activeChat.conversation_type) }}
                    </span>
                  </div>

                  <!-- Load Older Messages -->
                  <div class="text-center my-3" v-if="activeChatHasMore">
                    <v-btn
                      depressed
                      small
                      class="rounded-pill px-6 load-more-messages-btn"
                      :loading="isLoadingMoreMessages"
                      @click="loadMoreMessages(activeChat)"
                    >
                      <i class="far fa-history ml-2"></i>
                      عرض المزيد من الرسائل
                    </v-btn>
                  </div>

                  <!-- Active Chat View -->
                  <ChatComp
                    v-if="activeChatMessages.length"
                    :key="activeChat.key"
                    readOnly
                    :messages="activeChatMessages"
                  />

                  <div v-else class="chat-empty-state text-center py-5 px-3">
                    <div class="empty-icon-ring mb-3">
                      <i class="far fa-comment-alt-slash fa-2x"></i>
                    </div>
                    <h5 class="font-weight-bold text-dark mb-0">
                      لا توجد رسائل في هذه المحادثة
                    </h5>
                  </div>
                </div>
              </template>

              <!-- Creative Empty State -->
              <div v-else class="chat-empty-state text-center py-5 px-3">
                <div class="empty-icon-ring mb-3">
                  <i class="far fa-comment-alt-slash fa-2x"></i>
                </div>
                <h5 class="font-weight-bold text-dark mb-2">
                  لا توجد محادثات مسجلة لهذا الطلب
                </h5>
                <p class="text-muted small max-w-400 mx-auto mb-0">
                  لم يتم تبادل أي رسائل نصية أو وسائط بين العميل والسائق أثناء
                  تنفيذ هذا الطلب.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- ===== End Showing Order Details ===== -->

    <!-- ==================== Dialog Overlays ==================== -->

    <!-- 1. Finish Order Dialog -->
    <v-dialog v-model="showPayType" width="420">
      <v-card class="modern-dialog-card p-4">
        <form @submit.prevent="finishOrder">
          <h5 class="main_color text-center font-weight-bold mb-3">
            اختر طريقة الدفع (اختياري)
          </h5>
          <div class="input_wrapper top_label mb-3">
            <multiselect
              v-model="payment_type"
              :options="payment_types"
              label="name"
              track-by="name"
              placeholder=" "
              :searchable="true"
              :allow-empty="false"
              :show-labels="false"
            ></multiselect>
          </div>
          <div class="input_wrapper top_label mb-4">
            <textarea
              class="form-control custom-textarea-field"
              v-model="note"
              placeholder="الملاحظة..."
              rows="3"
              required
            />
            <label class="form-label"> الملاحظة </label>
          </div>
          <div class="text-center d-flex gap-2 justify-content-center">
            <v-btn
              depressed
              color="success"
              class="rounded-pill px-8 text-white"
              :loading="isFinishOrderLoading"
              type="submit"
            >
              إرسال
            </v-btn>
            <v-btn
              depressed
              class="rounded-pill px-6"
              @click="showPayType = false"
            >
              إلغاء
            </v-btn>
          </div>
        </form>
      </v-card>
    </v-dialog>

    <!-- 2. Driver Fine Dialog -->
    <v-dialog v-model="driverFineDialog" width="460">
      <v-card class="modern-dialog-card p-4">
        <form @submit.prevent="submitDriverFine">
          <h5 class="main_color text-center font-weight-bold mb-4">
            <i class="far fa-exclamation-triangle text-danger ml-2"></i>تطبيق
            مخالفة على السائق
          </h5>
          <div class="input_wrapper top_label mb-4">
            <label class="form-label font-weight-bold mb-1">السبب*</label>
            <multiselect
              v-model="fineReasonObj"
              :options="cancelReasons"
              label="name"
              track-by="id"
              placeholder="اختر السبب"
              :searchable="true"
              :allow-empty="false"
              :show-labels="false"
            ></multiselect>
          </div>
          <div class="input_wrapper top_label mb-4">
            <textarea
              class="form-control custom-textarea-field"
              v-model="fineNote"
              rows="3"
              placeholder="ملاحظات المخالفة..."
            />
            <label class="form-label">ملاحظة</label>
          </div>
          <div class="text-center d-flex gap-2 justify-content-center mt-4">
            <v-btn
              depressed
              color="error"
              class="rounded-pill px-8 text-white"
              :loading="isSubmittingFine"
              type="submit"
            >
              إرسال
            </v-btn>
            <v-btn
              depressed
              class="rounded-pill px-6"
              @click="driverFineDialog = false"
            >
              إلغاء
            </v-btn>
          </div>
        </form>
      </v-card>
    </v-dialog>

    <!-- 3. Balance Transfer Dialog -->
    <v-dialog v-model="isChangeBalanceModel" width="450">
      <v-card class="modern-dialog-card p-4">
        <form @submit.prevent="toggleBalance">
          <h5 class="main_color text-center font-weight-bold mb-3">
            <i class="far fa-wallet ml-2"></i>تحويل رصيد
          </h5>
          <div class="input_wrapper top_label mb-3">
            <label class="form-label"> نوع التحويل </label>
            <multiselect
              v-model="transaction.type"
              :options="types"
              label="name"
              track-by="name"
              placeholder="اختر نوع التحويل"
              :searchable="true"
              :allow-empty="false"
              :show-labels="false"
            ></multiselect>
          </div>
          <div class="input_wrapper top_label mb-3">
            <label class="form-label"> تحويل لـ </label>
            <multiselect
              v-model="user_type"
              :options="
                allData && allData.resturant_zoz
                  ? user_types
                  : user_types.filter((u) => u.id !== 'store')
              "
              label="name"
              track-by="name"
              placeholder="اختر المستخدم"
              :searchable="true"
              :allow-empty="false"
              :show-labels="false"
            ></multiselect>
          </div>
          <div class="input_wrapper top_label mb-3">
            <input
              step="any"
              type="number"
              class="form-control custom-input-field"
              v-model="transaction.amount"
              @input="helper_checkIfInputIsEmpty"
              placeholder="المبلغ..."
            />
            <label class="form-label"> المبلغ </label>
          </div>
          <div class="input_wrapper top_label mb-3">
            <input
              type="text"
              class="form-control custom-input-field"
              v-model="transaction.note"
              @input="helper_checkIfInputIsEmpty"
              placeholder="الملاحظة..."
            />
            <label class="form-label"> الملاحظة </label>
          </div>
          <div class="input_wrapper top_label mb-4">
            <textarea
              class="form-control custom-textarea-field"
              v-model="transaction.reason"
              placeholder="السبب..."
              rows="2"
            />
            <label class="form-label"> السبب </label>
          </div>
          <div class="text-center d-flex gap-2 justify-content-center">
            <v-btn
              depressed
              color="success"
              class="rounded-pill px-8 text-white"
              :loading="balanceLoading"
              type="submit"
            >
              تحويل
            </v-btn>
            <v-btn
              depressed
              class="rounded-pill px-6"
              @click="isChangeBalanceModel = false"
            >
              إلغاء
            </v-btn>
          </div>
        </form>
      </v-card>
    </v-dialog>

    <!-- 4. Cancel Order Dialog -->
    <v-dialog v-model="cancelDialog" max-width="480px">
      <v-card class="modern-dialog-card p-4">
        <h5 class="text-center font-weight-bold text-danger mb-3">
          <i class="far fa-exclamation-circle ml-2"></i>هل أنت متأكد من إلغاء
          الطلب؟
        </h5>
        <div class="form-group mb-4">
          <textarea
            v-model="cancel_reason"
            placeholder="أدخل سبب الإلغاء بالتفصيل..."
            class="form-control custom-textarea-field"
            rows="3"
          ></textarea>
        </div>
        <div class="d-flex justify-content-center gap-3">
          <v-btn
            color="error"
            depressed
            class="rounded-pill px-8 text-white"
            @click="cancelConfirm"
            :loading="cancelLoading"
          >
            نعم، تأكيد الإلغاء
          </v-btn>
          <v-btn
            color="grey lighten-2"
            depressed
            class="rounded-pill px-6"
            @click="cancelDialog = false"
          >
            إلغاء
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- 5. Raise Invoice Dialog -->
    <v-dialog v-model="invoiceDialog" width="460">
      <v-card class="modern-dialog-card p-4">
        <form @submit.prevent="submitInvoice">
          <h5 class="main_color text-center font-weight-bold mb-4">
            <i class="far fa-file-invoice-dollar ml-2"></i>
            {{ lang === "ar" ? "رفع فاتورة" : "Raise An Invoice" }}
          </h5>

          <!-- Enter cost -->
          <div class="input_wrapper top_label mb-4">
            <input
              type="number"
              step="any"
              class="form-control custom-input-field"
              v-model="invoiceCost"
              :placeholder="
                lang === 'ar' ? 'أدخل مبلغ الفاتورة' : 'Enter the cost'
              "
              required
            />
            <label class="form-label"
              >{{ lang === "ar" ? "مبلغ الفاتورة" : "Cost" }}*</label
            >
          </div>

          <!-- Order Cost Details Breakdown Preview -->
          <div class="border rounded-lg p-3 mb-4 bg-light small">
            <div class="d-flex justify-content-between mb-2">
              <span class="text-muted">{{
                lang === "ar" ? "قيمة التوصيل" : "Delivery Value"
              }}</span>
              <span class="font-weight-bold">{{
                allData?.shiping || allData?.delivery_price || 0
              }}</span>
            </div>
            <div class="d-flex justify-content-between mb-2">
              <span class="text-muted">{{
                lang === "ar" ? "الضريبة" : "VAT"
              }}</span>
              <span class="font-weight-bold">{{
                allData?.shiping_tax || allData?.tax || 0
              }}</span>
            </div>
            <div
              class="d-flex justify-content-between mb-2"
              v-if="allData?.service_fee"
            >
              <span class="text-muted">{{
                lang === "ar" ? "رسوم الخدمة" : "Service Fee"
              }}</span>
              <span class="font-weight-bold">{{ allData?.service_fee }}</span>
            </div>
            <div
              class="d-flex justify-content-between pt-2 border-top font-weight-bold"
            >
              <span>{{ lang === "ar" ? "الإجمالي" : "Total" }}</span>
              <span class="main_color">{{ allData?.total_price || 0 }}</span>
            </div>
          </div>

          <!-- Custom File Upload Field -->
          <div class="mb-4">
            <div
              class="custom-file-upload-box"
              :class="{ 'has-preview': invoiceImagePreview }"
            >
              <input
                type="file"
                ref="invoiceFileInput"
                accept="image/*"
                class="d-none"
                @change="onInvoiceFileChange"
              />
              <div
                v-if="!invoiceImagePreview"
                class="upload-placeholder p-4 text-center cursor-pointer"
                @click="$refs.invoiceFileInput.click()"
              >
                <i class="far fa-cloud-upload fa-2x mb-2 main_color"></i>
                <div>
                  <span class="font-weight-bold text-dark d-block">
                    {{
                      lang === "ar"
                        ? "انقر لاختيار صورة الفاتورة"
                        : "Choose Invoice Image"
                    }}
                  </span>
                  <span class="text-muted small">PNG, JPG up to 10MB</span>
                </div>
              </div>
              <div v-else class="upload-preview text-center position-relative">
                <img
                  :src="invoiceImagePreview"
                  class="img-fluid rounded border max-h-200"
                />
                <button
                  type="button"
                  class="remove-upload-btn"
                  @click="clearInvoiceFile"
                >
                  <i class="far fa-times"></i>
                </button>
              </div>
            </div>
          </div>

          <!-- Action buttons -->
          <div class="d-flex gap-2 justify-content-center">
            <v-btn
              depressed
              color="#783d73"
              class="text-white rounded-pill px-8"
              :loading="isSubmittingInvoice"
              type="submit"
            >
              {{ lang === "ar" ? "تأكيد" : "Confirm" }}
            </v-btn>
            <v-btn
              depressed
              class="rounded-pill px-6"
              @click="invoiceDialog = false"
            >
              {{ lang === "ar" ? "إلغاء" : "Cancel" }}
            </v-btn>
          </div>
        </form>
      </v-card>
    </v-dialog>

    <!-- 6. Return Order Dialog -->
    <v-dialog v-model="returnDialog" width="480">
      <v-card class="modern-dialog-card p-4">
        <form @submit.prevent="confirmReturnOrder">
          <h5 class="text-center font-weight-bold mb-4 text-warning">
            <i class="far fa-undo-alt ml-2"></i> إرجاع الطلب
          </h5>

          <!-- Return Preview (lines withdrawn from the restaurant wallet) -->
          <div
            v-if="allData && allData.return_preview"
            class="return-preview-box mb-4 p-3 rounded-lg border"
          >
            <h6 class="font-weight-bold small mb-2">
              <i class="far fa-receipt ml-1"></i> تفاصيل تكلفة الإرجاع
            </h6>
            <div
              v-for="(line, idx) in allData.return_preview.lines || []"
              :key="'return-line-' + idx"
              class="d-flex justify-content-between align-items-center small py-1"
            >
              <span>{{ line.title_trans || line.title }}</span>
              <span class="font-weight-bold">{{ line.amount }} ر.س</span>
            </div>
            <div
              class="d-flex justify-content-between align-items-center font-weight-bold border-top mt-2 pt-2"
            >
              <span>الإجمالي</span>
              <span class="text-danger"
                >{{ allData.return_preview.total }} ر.س</span
              >
            </div>
            <p class="text-muted small mt-2 mb-0">
              سيتم خصم كل بند من محفظة المطعم عند التنفيذ.
              <template v-if="isCashOnPickup">
                ويُودَع بند قيمة المنتجات فقط في محفظة المندوب.
              </template>
            </p>
          </div>

          <!-- Select Cancel/Return Reason -->
          <div class="input_wrapper top_label mb-4">
            <label class="form-label font-weight-bold mb-1"
              >سبب الإلغاء / الإرجاع *</label
            >
            <multiselect
              v-model="selectedReturnReason"
              :options="returnReasons"
              label="name"
              track-by="id"
              placeholder="اختر سبب الإرجاع..."
              :searchable="true"
              :allow-empty="false"
              :show-labels="false"
              :loading="isReturnReasonsLoading"
            ></multiselect>
          </div>

          <!-- Additional Notes -->
          <div class="input_wrapper top_label mb-4">
            <textarea
              class="form-control custom-textarea-field"
              v-model="other_text"
              rows="3"
              placeholder="أدخل ملاحظات إضافية عن الإرجاع (اختياري)..."
            ></textarea>
            <label class="form-label font-weight-bold">ملاحظات إضافية</label>
          </div>

          <div class="d-flex justify-content-center gap-3">
            <v-btn
              depressed
              color="#e65100"
              class="text-white rounded-pill px-8"
              :loading="isReturnOrderLoading"
              type="submit"
            >
              تأكيد الإرجاع
            </v-btn>
            <v-btn
              depressed
              color="grey lighten-2"
              class="rounded-pill px-6"
              @click="returnDialog = false"
            >
              إلغاء
            </v-btn>
          </div>
        </form>
      </v-card>
    </v-dialog>

    <!-- 7. Settle and Finish Order Dialog (Super Creative Cockpit Design with Sticky Footer & Smooth Scroll) -->
    <v-dialog
      v-model="settleDialog"
      width="580"
      content-class="settle-finish-super-modal"
      :persistent="isSettleLoading"
    >
      <v-card class="settle-creative-card position-relative d-flex flex-column">
        <!-- Top Glow Accent Bar -->
        <div class="modal-glow-top-bar flex-shrink-0"></div>

        <!-- Floating Close Button -->
        <button
          type="button"
          class="modal-floating-close-btn"
          @click.stop="settleDialog = false"
          :disabled="isSettleLoading"
          title="إغلاق"
        >
          <i class="far fa-times"></i>
        </button>

        <!-- Scrollable Modal Content Body -->
        <div class="settle-dialog-scroll-body flex-grow-1 p-4 p-md-5 pb-3">
          <!-- Hero Header Section -->
          <div class="text-center mb-4">
            <div class="settle-hero-badge-wrap mx-auto mb-3">
              <span class="settle-hero-pulse-ring"></span>
              <div class="settle-hero-icon-box">
                <i class="fas fa-hand-holding-usd"></i>
              </div>
            </div>

            <div class="mb-2">
              <span class="settle-tag-pill">
                <i class="fas fa-sparkles ml-1"></i> تسوية مالية وإغلاق فوري
              </span>
            </div>

            <h4 class="settle-modal-title mb-1 font-weight-bolder text-dark">
              تسوية وإنهاء الطلب
            </h4>
            <p class="settle-modal-desc text-muted small mb-0">
              راجع العمليات المالية المقيدة بين أطراف الطلب قبل الإغلاق النهائي
              وتأكيد العملية
            </p>
          </div>

          <!-- Settlement Preview Info -->
          <div
            v-if="allData && allData.settlement_preview"
            class="settlement-preview-wrapper mb-3"
          >
            <!-- Settlement Total Showcase Card -->
            <div
              v-if="allData.settlement_preview.settlement_total != null"
              class="settle-hero-cockpit-card mb-4 p-4 rounded-2xl"
            >
              <div
                class="cockpit-top d-flex align-items-center justify-content-between mb-2"
              >
                <span class="cockpit-label">
                  <i class="fas fa-coins ml-1 text-teal"></i> إجمالي مبلغ
                  التسوية المستحق
                </span>
                <span class="cockpit-badge">
                  <i class="fas fa-shield-alt ml-1"></i> قيود مؤكدة
                </span>
              </div>

              <div class="cockpit-amount-row text-center py-2">
                <span class="cockpit-amount-val font-weight-bolder">
                  {{ allData.settlement_preview.settlement_total }}
                </span>
                <span class="cockpit-currency mr-2 font-weight-bold">ر.س</span>
              </div>

              <!-- Visual Flow Pipeline between Store & Driver -->
              <div class="settle-flow-track pt-3 mt-2 border-top-soft">
                <div
                  class="d-flex align-items-center justify-content-between px-2"
                >
                  <!-- Store side (Right in RTL) -->
                  <div
                    class="flow-party party-sender d-flex align-items-center gap-2"
                  >
                    <div class="party-mini-icon icon-deduct">
                      <i class="fas fa-store"></i>
                    </div>
                    <div class="text-right">
                      <span class="party-name d-block font-weight-bold"
                        >المطعم / المتجر</span
                      >
                      <span
                        class="party-sub-status text-danger small font-weight-bold"
                        >خصم من الرصيد</span
                      >
                    </div>
                  </div>

                  <!-- Animated Flow Connector -->
                  <div
                    class="flow-direction-connector flex-grow-1 px-3 text-center"
                  >
                    <div class="flow-animated-line">
                      <span class="flow-dot-pulse"></span>
                      <i class="fas fa-long-arrow-alt-left flow-arrow-icon"></i>
                    </div>
                    <span class="flow-transfer-tag">تحويل مباشر</span>
                  </div>

                  <!-- Driver side (Left in RTL) -->
                  <div
                    class="flow-party party-receiver d-flex align-items-center gap-2"
                  >
                    <div class="text-left">
                      <span class="party-name d-block font-weight-bold"
                        >السائق</span
                      >
                      <span
                        class="party-sub-status text-success small font-weight-bold"
                        >إيداع بالمحفظة</span
                      >
                    </div>
                    <div class="party-mini-icon icon-receive">
                      <i class="fas fa-motorcycle"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Itemized Transactions Breakdown Lines -->
            <div
              v-if="
                allData.settlement_preview.lines &&
                allData.settlement_preview.lines.length
              "
              class="settlement-lines-container"
            >
              <div
                class="d-flex align-items-center justify-content-between mb-3"
              >
                <h6 class="lines-title font-weight-bold text-dark mb-0 small">
                  <i class="far fa-receipt ml-1 text-teal"></i>
                  تفاصيل المعاملات المقيدة بالطلب:
                </h6>
                <span class="badge-operations-counter small">
                  {{ allData.settlement_preview.lines.length }} عمليات مالية
                </span>
              </div>

              <div class="settlement-lines-list">
                <div
                  v-for="(line, idx) in allData.settlement_preview.lines"
                  :key="idx"
                  class="settle-line-card d-flex align-items-center justify-content-between p-3 mb-2 rounded-xl"
                  :class="`line-${line.direction}`"
                >
                  <div class="d-flex align-items-center gap-3">
                    <div
                      class="line-side-avatar"
                      :class="
                        line.direction === 'withdrawal'
                          ? 'avatar-deduct'
                          : 'avatar-credit'
                      "
                    >
                      <i :class="getUserTypeIcon(line.user_type)"></i>
                    </div>
                    <div>
                      <div
                        class="d-flex align-items-center gap-2 mb-1 flex-wrap"
                      >
                        <span
                          class="line-user-title font-weight-bold text-dark"
                        >
                          {{ getUserTypeLabel(line.user_type) }}
                        </span>
                        <span
                          class="line-pill-badge"
                          :class="
                            line.direction === 'withdrawal'
                              ? 'pill-deduct'
                              : 'pill-credit'
                          "
                        >
                          <i
                            :class="
                              line.direction === 'withdrawal'
                                ? 'fas fa-arrow-down'
                                : 'fas fa-arrow-up'
                            "
                            class="ml-1"
                          ></i>
                          {{
                            line.direction === "withdrawal"
                              ? "خصم من الرصيد"
                              : "إيداع في المحفظة"
                          }}
                        </span>
                      </div>
                      <div class="line-meta-desc text-muted small">
                        <i class="far fa-file-invoice ml-1"></i>
                        {{ line.title_trans || line.title || "تسوية الطلب" }}
                      </div>
                    </div>
                  </div>

                  <div class="line-amount-box text-left">
                    <div
                      class="line-amount-badge"
                      :class="
                        line.direction === 'withdrawal'
                          ? 'amount-negative'
                          : 'amount-positive'
                      "
                    >
                      <span class="amount-sign">{{
                        line.direction === "withdrawal" ? "-" : "+"
                      }}</span>
                      <span class="amount-number">{{ line.amount }}</span>
                      <span class="amount-curr">ر.س</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Fallback if no settlement_preview available -->
          <div
            v-else
            class="settle-empty-alert text-center rounded-xl p-4 mb-4"
          >
            <i class="fas fa-info-circle ml-1 text-teal"></i>
            سيتم احتساب وتطبيق التسوية المالية تلقائياً وإغلاق الطلب فور
            التأكيد.
          </div>

          <!-- CASH_ON_PICKUP note: product value excluded -->
          <div
            v-if="isCashOnPickup"
            class="settle-empty-alert rounded-xl p-3 mb-3 small"
          >
            <i class="fas fa-info-circle ml-1 text-teal"></i>
            هذا الطلب بنظام الدفع نقدًا عند الاستلام (CASH_ON_PICKUP): قيمة
            المنتجات مستبعدة من التسوية، وقد يكون مبلغ التسوية صفرًا ويظل
            الإنهاء متاحًا.
          </div>

          <!-- System Guarantee / Safety Notice -->
          <div
            class="settle-safety-note d-flex align-items-center gap-2 p-3 mb-1 rounded-xl"
          >
            <div class="safety-icon-ring">
              <i class="fas fa-shield-check text-teal"></i>
            </div>
            <div class="safety-text small text-muted">
              عملية التسوية فورية وتتم في المحافظ المالية مباشرة، وسيتم تحويل
              حالة الطلب إلى مكتمل بنجاح.
            </div>
          </div>
        </div>

        <!-- Sticky Footer for Action Buttons (Always Visible at Bottom) -->
        <div
          class="settle-dialog-sticky-footer p-3 px-4 px-md-5 d-flex align-items-center justify-content-center gap-3 flex-shrink-0"
        >
          <v-btn
            depressed
            height="48"
            class="settle-action-confirm-btn text-white rounded-pill px-8 font-weight-bold"
            :loading="isSettleLoading"
            :disabled="isSettleLoading"
            @click="confirmSettleAndFinish"
          >
            <i class="fas fa-check-circle ml-2"></i>
            تأكيد التسوية والإنهاء
          </v-btn>
          <v-btn
            depressed
            height="48"
            class="settle-action-cancel-btn rounded-pill px-6 font-weight-bold"
            :disabled="isSettleLoading"
            @click.stop="settleDialog = false"
          >
            إلغاء
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- Live Support Chat (client / driver / store of this order) -->
    <FloatingChat
      v-if="supportChat"
      :key="`${supportChat.userType}-${supportChat.supportChatId}`"
      :userId="supportChat.userId"
      :supportChatId="supportChat.supportChatId"
      :userType="supportChat.userType"
      :userName="supportChat.userName"
      :userAvatar="supportChat.userAvatar"
      @close="supportChat = null"
    />
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import ChatComp from "@/components/Global/ChatCompOrder";
import FloatingChat from "@/components/Chat/FloatingChat.vue";
import OrderCandidates from "@/components/Global/OrderCandidates";
import sportCarImage from "@/assets/images/sport-car.png";
import imageFalse from "@/assets/images/icons/logo.png";
import io from "socket.io-client";
import {
  createLiveTimeline,
  TICK_MS,
  COUNTDOWN_MS,
  describeStep,
  stepColor,
  isStepDelayed,
} from "@/utils/delayTimelineLive";

export default {
  props: ["id"],
  name: "CarRental",

  components: {
    ChatComp,
    OrderCandidates,
    FloatingChat,
  },

  data() {
    return {
      // ========== Live support chat (FloatingChat) target
      supportChat: null,

      // ========== Active Log Tab State
      activeLogTab: "wallet", // 'wallet' | 'logs' | 'calls'

      // ========== Breadcrumbs
      items: [
        {
          text: this.$t("breadcrumb.mainPage"),
          disabled: false,
          href: "/",
        },
        {
          text: this.$t("breadcrumb.orders.delivery_orders"),
          disabled: true,
          href: "/delivery_orders/show-all",
        },
      ],

      // ========== Your Data
      allData: null, // injected in created
      currentTime: new Date(),
      countdownInterval: null,

      // ========== Loading
      loading: false,
      reportReasonToShow: null,
      btnIsLoading: false,
      showPayType: false,
      drivers: [],
      driversLoading: false,
      selectedDriverId: null,
      driverSearchText: null,
      driverSearchTimeout: null,
      isAssigningDriver: false,
      cancelDialog: false,
      cancelLoading: false,
      cancel_reason: null,
      isFinishOrderLoading: false,
      isChangeStatusLoading: false,
      isReturnOrderLoading: false,
      returnDialog: false,
      settleDialog: false,
      isSettleLoading: false,
      isReceivingSupport: false,
      selectedReturnReason: null,
      returnReasons: [],
      isReturnReasonsLoading: false,
      other_text: "",
      feedbackText: "",
      isSubmittingFeedback: false,
      commentCopied: false,
      commentCopyTimer: null,
      chatData: null,
      chatsData: [],
      activeChatIndex: 0,
      isLoadingMoreMessages: false,
      chatMessagesPerPage: 10,
      isChangeBalanceModel: false,
      balanceLoading: false,
      driverFineDialog: false,
      fineReasonObj: null,
      cancelReasons: [],
      fineNote: "",
      isSubmittingFine: false,
      transaction: {
        type: null,
        amount: null,
        note: null,
        reason: null,
      },
      types: [
        {
          id: "withdraw",
          name: "سحب",
        },
        {
          id: "charge",
          name: "ايداع",
        },
      ],
      payment_types: [
        {
          id: "cash",
          name: "كاش",
        },
      ],
      user_types: [
        {
          id: "user",
          name: "العميل",
        },
        {
          id: "driver",
          name: "السائق",
        },
        {
          id: "store",
          name: "المتجر",
        },
      ],
      user_type: null,
      payment_type: {
        id: "cash",
        name: "كاش",
      },
      note: null,
      invoiceDialog: false,
      invoiceCost: "",
      invoiceFile: null,
      invoiceImagePreview: null,
      isSubmittingInvoice: false,
      isUploadingImage: false,
      //tracking
      clickedLatLng: null,
      marker: null,
      center: {
        lat: 24.7136,
        lng: 46.7385,
      },
      image: {
        url: sportCarImage,
        size: { width: 32, height: 32 },
      },
    };
  },

  computed: {
    ...mapGetters({
      lang: "lang_module/lang",
    }),
    isCashOnPickup() {
      const d = this.allData || {};
      return (
        (d.purchase_settlement_method ||
          (d.resturant_zoz && d.resturant_zoz.purchase_settlement_method)) ===
        "CASH_ON_PICKUP"
      );
    },
    headers() {
      return [
        {
          text: "#",
          align: "center",
          value: "index",
          sortable: true,
          width: "80",
        },
        {
          text: "المنتج",
          align: "center",
          value: "meal",
          sortable: false,
        },
        {
          text: "الكمية",
          align: "center",
          value: "quantity",
          sortable: false,
        },
        {
          text: "السعر",
          align: "center",
          value: "price",
          sortable: false,
        },
        {
          text: "السعر الإجمالي",
          align: "center",
          value: "total_price",
          sortable: false,
        },
      ];
    },
    headers2() {
      return [
        {
          text: "#",
          align: "center",
          value: "index",
          sortable: true,
          width: "80",
        },
        {
          text: "العنوان",
          align: "center",
          value: "title_trans",
          sortable: false,
        },
        {
          text: "الكمية",
          align: "center",
          value: "amount",
          sortable: false,
        },
        {
          text: "النوع",
          align: "center",
          value: "type",
          sortable: false,
        },
        {
          text: "نوع المستخدم",
          align: "center",
          value: "user.user_type",
          sortable: false,
        },
        {
          text: " التاريخ",
          align: "center",
          value: "created_at",
          sortable: false,
        },
        {
          text: " الحالة",
          align: "center",
          value: "status",
          sortable: false,
        },
      ];
    },
    orderCandidates() {
      const d = this.allData || {};
      const list = d.candidates || d.driver_candidates || [];
      return Array.isArray(list) ? list : [];
    },
    headers3() {
      return [
        {
          text: "#",
          align: "center",
          value: "index",
          sortable: true,
          width: "80",
        },
        {
          text: "الموظف",
          align: "center",
          value: "user.full_name",
          sortable: false,
        },
        {
          text: "العملية",
          align: "center",
          value: "log",
          sortable: false,
        },
        {
          text: "النوع",
          align: "center",
          value: "type",
          sortable: false,
        },
        {
          text: " التاريخ",
          align: "center",
          value: "created_at",
          sortable: false,
        },
      ];
    },
    // Who carries the delivery cost on an on demand rider order
    deliveryCostOnLabel() {
      const labels = {
        client: "العميل",
        restaurant: "المطعم",
        store: "المتجر",
        resturant: "المطعم",
        zoz: "المنصة",
        admin: "المنصة",
      };
      const costOn = this.allData?.delivery_cost_on;
      return labels[costOn] || costOn || "لا يوجد";
    },

    orderChats() {
      return Array.isArray(this.chatsData) ? this.chatsData : [];
    },

    activeChat() {
      return this.orderChats[this.activeChatIndex] || null;
    },

    activeChatMessages() {
      return (this.activeChat && this.activeChat.messages) || [];
    },

    activeChatHasMore() {
      return !!(this.activeChat && this.activeChat.has_more_messages);
    },

    headers4() {
      return [
        {
          text: "#",
          align: "center",
          value: "index",
          sortable: false,
          width: "60",
        },
        {
          text: "المتصل",
          align: "center",
          value: "caller",
          sortable: false,
        },
        {
          text: "نوع المتصل",
          align: "center",
          value: "caller.user_type",
          sortable: false,
        },
        {
          text: "المستقبل",
          align: "center",
          value: "receiver",
          sortable: false,
        },
        {
          text: "نوع العملية",
          align: "center",
          value: "action_type",
          sortable: false,
        },
        {
          text: "نص الرسالة",
          align: "center",
          value: "message_body",
          sortable: false,
        },
        {
          text: "التاريخ",
          align: "center",
          value: "created_at",
          sortable: false,
        },
      ];
    },
    canAssignDriver() {
      if (!this.allData) return false;
      const status = this.allData.status;
      const excludedStatuses = [
        "driver_finished_delivery",
        "client_cancel",
        "restaurant_reject",
        "restaurant_cancel",
        "admin_cancel",
      ];
      if (excludedStatuses.includes(status)) {
        return false;
      }
      if (this.allData.resturant_zoz && status === "pending") {
        return false;
      }
      return true;
    },

    isScheduledOrder() {
      return !!(
        this.allData &&
        (this.allData.take_date || this.allData.take_time)
      );
    },

    hasOrderComment() {
      const c = this.allData?.comment;
      return c !== null && c !== undefined && String(c).trim() !== "";
    },

    scheduledPeriodCode() {
      const p = (this.allData?.take_preiod || this.allData?.take_period || "")
        .toString()
        .toLowerCase();
      if (p === "am" || p.includes("am") || p.includes("صباح")) return "am";
      if (p === "pm" || p.includes("pm") || p.includes("مساء")) return "pm";
      if (this.scheduledTargetDate) {
        return this.scheduledTargetDate.getHours() >= 12 ? "pm" : "am";
      }
      return "pm";
    },

    scheduledPeriodLabel() {
      return this.scheduledPeriodCode === "am" ? "صباحاً" : "مساءً";
    },

    scheduledTargetDate() {
      if (!this.isScheduledOrder) return null;
      return this.parseScheduleDate(
        this.allData.take_date,
        this.allData.take_time,
        this.allData.take_preiod || this.allData.take_period
      );
    },

    scheduledRelativeDateLabel() {
      if (!this.scheduledTargetDate) return this.allData?.take_date || "";
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const targetDay = new Date(
        this.scheduledTargetDate.getFullYear(),
        this.scheduledTargetDate.getMonth(),
        this.scheduledTargetDate.getDate()
      );
      const diffDays = Math.round((targetDay - today) / (1000 * 60 * 60 * 24));

      const options = {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      };
      const formattedFull = this.scheduledTargetDate.toLocaleDateString(
        "ar-EG",
        options
      );

      if (diffDays === 0) return `اليوم (${formattedFull})`;
      if (diffDays === 1) return `غداً (${formattedFull})`;
      if (diffDays === 2) return `بعد يومين (${formattedFull})`;
      if (diffDays < 0) return `سابق (${formattedFull})`;
      return formattedFull;
    },

    scheduleCountdown() {
      if (!this.scheduledTargetDate) return null;

      const now = this.currentTime || new Date();
      const diffMs = this.scheduledTargetDate.getTime() - now.getTime();
      const isPast = diffMs <= 0;
      const absDiffMs = Math.abs(diffMs);

      const days = Math.floor(absDiffMs / (1000 * 60 * 60 * 24));
      const hours = Math.floor(
        (absDiffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
      );
      const minutes = Math.floor((absDiffMs % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((absDiffMs % (1000 * 60)) / 1000);
      const totalMinutes = Math.floor(absDiffMs / (1000 * 60));

      const isCompleted = [
        "driver_finished_delivery",
        "client_cancel",
        "admin_cancel",
        "restaurant_cancel",
        "restaurant_reject",
      ].includes(this.allData?.status);

      let urgencyState = "normal"; // 'normal' | 'soon' | 'urgent' | 'due' | 'completed'
      let statusHeadline = "مجدول (في انتظار موعد البدء)";
      let stepProgress = 1;

      if (isCompleted) {
        urgencyState = "completed";
        statusHeadline = "طلب مجدول مكتمل";
        stepProgress = 4;
      } else if (isPast) {
        urgencyState = "due";
        statusHeadline = "حان موعد بدء الطلب الآن";
        stepProgress = 3;
      } else if (diffMs <= 15 * 60 * 1000) {
        urgencyState = "urgent";
        statusHeadline = "موعد بدء الطلب وشيك (أقل من 15 دقيقة)";
        stepProgress = 2;
      } else if (diffMs <= 60 * 60 * 1000) {
        urgencyState = "soon";
        statusHeadline = "اقترب موعد بدء الطلب (خلال ساعة)";
        stepProgress = 2;
      }

      if (
        this.allData?.driver?.id ||
        this.allData?.status === "driver_accept" ||
        this.allData?.status === "driver_start_order" ||
        this.allData?.status === "driver_pre_finished"
      ) {
        stepProgress = Math.max(stepProgress, 4);
      } else if (
        this.allData?.status === "restaurant_accept" ||
        this.allData?.status === "restaurant_ready"
      ) {
        stepProgress = Math.max(stepProgress, 4);
      }

      return {
        diffMs,
        isPast,
        days,
        hours,
        minutes,
        seconds,
        totalMinutes,
        urgencyState,
        statusHeadline,
        stepProgress,
        isCompleted,
      };
    },

    scheduledSmartAdvice() {
      if (!this.scheduleCountdown) return "";
      const { diffMs, isPast, urgencyState } = this.scheduleCountdown;
      const hasDriver = !!(this.allData?.driver?.id || this.allData?.driver_id);

      if (urgencyState === "completed") {
        return "تم إنجاز وتسليم هذا الطلب المجدول بنجاح.";
      }
      if (isPast) {
        return "حان موعد بدء تنفيذ الطلب المجدول! يرجى التأكد من قبول وتجهيز الطلب وإسناد السائق.";
      }
      if (diffMs <= 15 * 60 * 1000) {
        return hasDriver
          ? "موعد بدء الطلب وشيك جداً (أقل من 15 دقيقة) - السائق مسند وجاهز للانطلاق فور حلول الموعد."
          : "موعد بدء الطلب وشيك جداً (أقل من 15 دقيقة) - يرجى الاستعداد لإطلاق وتجهيز الطلب وتعيين سائق.";
      }
      if (diffMs <= 60 * 60 * 1000) {
        return "متبقي أقل من ساعة على موعد بدء تنفيذ الطلب.";
      }
      return "الطلب في وضع الجدولة المسبقة. سيبدأ تنفيذه في الموعد المحدد.";
    },
  },

  watch: {
    driverSearchText(val) {
      if (this.isAssigningDriver) return;
      if (this.selectedDriverId) {
        const currentDriver = this.drivers.find(
          (d) => d.id === this.selectedDriverId
        );
        if (currentDriver && currentDriver.name === val) {
          return;
        }
      }

      if (this.driverSearchTimeout) {
        clearTimeout(this.driverSearchTimeout);
      }
      this.driverSearchTimeout = setTimeout(() => {
        this.getDrivers(val ? val.trim() : "");
      }, 400);
    },

    invoiceDialog(val) {
      if (!val) {
        this.invoiceCost = "";
        this.invoiceFile = null;
        this.invoiceImagePreview = null;
        if (this.$refs.invoiceFileInput) {
          this.$refs.invoiceFileInput.value = "";
        }
      }
    },
  },

  methods: {
    // getWalletRowClass(item) {
    //   if (!item) return "";
    //   const type = item.user?.user_type || item.user_type || "";
    //   if (type === "driver") return "wallet-row-driver";
    //   if (type === "restaurant" || type === "store")
    //     return "wallet-row-restaurant";
    //   if (type === "client" || type === "user") return "wallet-row-client";
    //   return "";
    // },

    getStatusTimeLabel(key) {
      const statusLabels = {
        pending: "قيد الانتظار",
        client_cancel: "تم الإلغاء من قبل العميل",
        admin_cancel: "تم الإلغاء من قبل الإدارة",
        admin_returned: "تم الإرجاع من قبل الإدارة",
        restaurant_accept: "تم القبول من قبل المطعم",
        restaurant_reject: "تم الرفض من قبل المطعم",
        restaurant_ready: "تم تجهيز الطلب من المطعم",
        driver_accept: "تم القبول من السائق",
        driver_arrived: "وصل السائق للمتجر",
        driver_received: "استلم السائق الطلب",
        driver_start_order: "بدأ السائق",
        driver_pre_finished: "السائق أوشك على الانتهاء",
        driver_finished_delivery: "تم الانتهاء",
        driver_finished_package: "تم الانتهاء",
        driver_cancel: "تم الإلغاء من قبل السائق",
        driver_reject: "تم الرفض من قبل السائق",
        driver_rejected: "تم الرفض من قبل السائق",
        paid: "تم الدفع",
      };
      return statusLabels[key] || key.replace(/_/g, " ");
    },
    formatStatusTime(time) {
      if (!time) return "-";
      return new Date(time).toLocaleString();
    },
    getDelayStepLabel(key) {
      const delayStepLabels = {
        support_received: "استلام الموظف",
        driver_assignment_delay: "تأخير تعيين السائق",
        driver_arrival_delay: "تأخير وصول السائق",
        order_pickup_delay: "تأخير استلام الطلب",
        order_finish_delay: "تأخير انهاء الطلب",
        order_delay_percentage: "نسبة تأخير الطلب ٪",
      };
      return delayStepLabels[key] || (key ? key.replace(/_/g, " ") : "");
    },
    describeStep,
    stepColor,
    isStepDelayed,
    async receiveBySupport(step) {
      if (!this.allData?.id || (step && step.is_received)) return;
      try {
        this.isReceivingSupport = true;
        const { data } = await this.$axios.post(
          `delivery_order/${this.allData.id}/receive-by-support`
        );
        this.applySupportReceivedStep(data?.data);
        this.$iziToast.success({
          timeout: 2000,
          message: data?.message || "تم استلام الطلب",
          position: "bottomRight",
        });
      } catch (err) {
        this.$iziToast.error({
          timeout: 2000,
          message:
            (err.response && err.response.data && err.response.data.message) ||
            "حدث خطأ أثناء استلام الطلب",
          position: "bottomRight",
        });
      } finally {
        this.isReceivingSupport = false;
      }
    },
    applyDelayTimelineUpdate(newSteps) {
      const steps = this.allData?.delay_timeline?.steps;
      if (!Array.isArray(newSteps) || !Array.isArray(steps)) return;
      newSteps.forEach((newStep) => {
        if (!newStep || !newStep.key) return;
        const idx = steps.findIndex((s) => s.key === newStep.key);
        if (idx > -1) {
          this.$set(steps, idx, { ...steps[idx], ...newStep });
        }
        if (newStep.key === "support_received") {
          if (newStep.support_employee_name !== undefined) {
            this.allData.support_employee_name = newStep.support_employee_name;
          }
          if (newStep.received_at !== undefined) {
            this.allData.support_received_at = newStep.received_at;
          }
        }
      });
    },
    applySupportReceivedStep(data) {
      if (!data || !this.allData) return;
      if (data.support_employee_name !== undefined) {
        this.allData.support_employee_name = data.support_employee_name;
      }
      if (data.support_received_at !== undefined) {
        this.allData.support_received_at = data.support_received_at;
      }
      // Refresh every step and the server totals from the response; later
      // socket snapshots reconcile it further.
      const timeline = data.delay_timeline;
      if (
        Array.isArray(timeline?.steps) &&
        this.allData.delay_timeline &&
        !this.live.applySnapshot(this, this.allData, data)
      ) {
        this.applyDelayTimelineUpdate(timeline.steps);
        ["total_delay_minutes", "delay_percentage"].forEach((k) => {
          if (timeline[k] !== undefined) {
            this.$set(this.allData.delay_timeline, k, timeline[k]);
          }
        });
        this.live.forget(this.allData.id);
      }
      if (Array.isArray(data.order_logs) && data.order_logs.length) {
        const existingLogs = this.allData.order_logs || [];
        const hasSupportLog = existingLogs.some(
          (l) => l.type === "support_receive_order"
        );
        if (!hasSupportLog) {
          this.allData.order_logs = [...data.order_logs, ...existingLogs];
        }
      }
    },
    getWalletRowClass(item) {
      if (item.user && item.user.user_type) {
        switch (item.user.user_type) {
          case "driver":
            return "wallet-row-driver";
          case "client":
            return "wallet-row-client";
          case "restaurant":
            return "wallet-row-restaurant";
          default:
            return "";
        }
      }
      return "";
    },
    handleMapClick(event) {
      this.clickedLatLng = {
        lat: event.latLng.lat(),
        lng: event.latLng.lng(),
      };

      setTimeout(() => {
        this.mapLogic();
      }, 2000);
    },
    async finishOrder() {
      try {
        this.isFinishOrderLoading = true;
        const requestData = {};
        if (this.payment_type && this.payment_type.id) {
          requestData.payment_type = this.payment_type.id;
          requestData.reason_of_finished = this.note;
        }

        const { data } = await this.$axios.post(
          `order/finished_${
            this.allData.delivery_type == "google_restaurant" ? "google" : "zoz"
          }/${this.allData?.id}`,
          requestData
        );
        this.$iziToast.success({
          timeout: 2000,
          message: data.message,
          position: "bottomRight",
        });
        this.isFinishOrderLoading = false;
        this.showPayType = false;
        this.setRows();
      } catch (err) {
        this.$iziToast.error({
          timeout: 2000,
          message: err.response.data.message,
          position: "bottomRight",
        });
        this.isFinishOrderLoading = false;
        this.showPayType = false;
      }
    },
    getReturnReasons() {
      this.isReturnReasonsLoading = true;
      this.$axios({
        method: "GET",
        url: "cancel_reason_not_paginated",
        params: {
          reason_type: "return",
        },
      })
        .then((res) => {
          this.returnReasons = res.data.data || [];
          this.isReturnReasonsLoading = false;
        })
        .catch((err) => {
          console.error(err);
          this.isReturnReasonsLoading = false;
        });
    },
    openReturnDialog() {
      this.selectedReturnReason = null;
      this.other_text = "";
      this.returnDialog = true;
      this.getReturnReasons();
    },
    async confirmReturnOrder() {
      if (!this.selectedReturnReason) {
        this.$iziToast.error({
          timeout: 3000,
          message: "يرجى اختيار سبب الإلغاء / الإرجاع أولاً",
          position: "bottomRight",
        });
        return;
      }
      try {
        this.isReturnOrderLoading = true;
        const formData = new FormData();
        const reasonId =
          typeof this.selectedReturnReason === "object"
            ? this.selectedReturnReason.id
            : this.selectedReturnReason;

        formData.append("cancel_reason_id", reasonId);

        if (this.other_text && this.other_text.trim()) {
          formData.append("other_text", this.other_text.trim());
        }

        const { data } = await this.$axios.post(
          `order/${this.allData.id}/return`,
          formData
        );
        this.$iziToast.success({
          timeout: 3000,
          message: data.message || "تم إرجاع الطلب بنجاح",
          position: "bottomRight",
        });
        this.isReturnOrderLoading = false;
        this.returnDialog = false;
        this.setRows();
      } catch (err) {
        this.isReturnOrderLoading = false;
        this.$iziToast.error({
          timeout: 3000,
          message:
            err.response && err.response.data
              ? err.response.data.message
              : "حدث خطأ أثناء إرجاع الطلب",
          position: "bottomRight",
        });
      }
    },
    getUserTypeLabel(type) {
      if (!type) return "طرف الطلب";
      const t = String(type).toLowerCase();
      if (t === "restaurant") return "المطعم";
      if (t === "store") return "المتجر";
      if (t === "driver") return "السائق";
      if (t === "client" || t === "user") return "العميل";
      return type;
    },
    getUserTypeIcon(type) {
      if (!type) return "far fa-user";
      const t = String(type).toLowerCase();
      if (t === "restaurant") return "far fa-utensils";
      if (t === "store") return "far fa-store";
      if (t === "driver") return "far fa-motorcycle";
      if (t === "client" || t === "user") return "far fa-user";
      return "far fa-user-circle";
    },
    async confirmSettleAndFinish() {
      try {
        this.isSettleLoading = true;
        const targetId = this.allData?.id || this.id;
        const { data } = await this.$axios.post(
          `order/${targetId}/settle-and-finish`
        );
        this.$iziToast.success({
          timeout: 3000,
          message: data.message || "تم تسوية وإنهاء الطلب بنجاح",
          position: "bottomRight",
        });
        this.settleDialog = false;
        this.setRows();
      } catch (err) {
        const errMsg =
          (err.response && err.response.data && err.response.data.message) ||
          "حدث خطأ أثناء تسوية وإنهاء الطلب";
        this.$iziToast.error({
          timeout: 5000,
          message: errMsg,
          position: "bottomRight",
        });
      } finally {
        this.isSettleLoading = false;
      }
    },
    toggleBalance() {
      let user_id;
      if (this.user_type && this.user_type.id == "user") {
        user_id = this.allData.client?.id;
      } else if (this.user_type && this.user_type.id == "driver") {
        user_id = this.allData.driver?.id;
      } else if (this.user_type && this.user_type.id == "store") {
        if (this.allData.resturant_zoz?.id) {
          user_id = this.allData.resturant_zoz?.id;
        }
      }
      this.balanceLoading = true;
      if (!this.transaction.type) {
        this.$iziToast.error({
          timeout: 2000,
          message: "ادخل نوع التحويل",
          position: "bottomCenter",
        });
        this.balanceLoading = false;
        return;
      }
      if (!this.transaction.amount) {
        this.balanceLoading = false;
        return;
      }
      const formDta = new FormData();
      if (this.transaction.type) {
        formDta.append("type", this.transaction.type?.id);
      }

      formDta.append("model_type", "delivery");
      formDta.append("model_id", this.id);
      formDta.append("amount", this.transaction.amount);
      if (this.transaction.note) {
        formDta.append("note", this.transaction.note);
      }
      if (this.transaction.reason) {
        formDta.append("reason", this.transaction.reason);
      }

      this.$axios
        .post(`${this.transaction.type?.id}/${user_id}`, formDta)
        .then((res) => {
          this.balanceLoading = false;
          this.isChangeBalanceModel = false;
          this.transaction.amount = null;
          this.transaction.type = null;
          this.user_type = null;
          this.transaction.note = null;
          this.transaction.reason = null;
          this.$iziToast.success({
            timeout: 2000,
            message: res.data.message,
            position: "bottomCenter",
          });
          this.setRows();
        })
        .catch((err) => {
          this.balanceLoading = false;
          this.$iziToast.error({
            timeout: 2000,
            message: err.response.data.message,
            position: "bottomCenter",
          });
        });
    },
    cancelOrder() {
      this.cancelDialog = true;
    },
    cancelConfirm() {
      if (!this.cancel_reason) {
        this.$iziToast.error({
          timeout: 5000,
          message: "يرجي ادخال سبب الإلغاء",
          position: "bottomRight",
        });
        return;
      } else {
        this.cancelLoading = true;
        this.$axios
          .post(`order/${this.id}/cancel`, {
            order_type: "Delivery",
            cancel_text: this.cancel_reason,
          })
          .then(() => {
            this.cancelLoading = false;
            this.cancelDialog = false;
            this.setRows();
          })
          .catch(() => {
            this.cancelLoading = false;
          });
      }
    },

    replaceImage(e) {
      e.target.src = imageFalse;
    },

    onDriverSelectClick() {
      if (!this.driverSearchText) {
        this.getDrivers("");
      }
    },
    clearDriverSearch() {
      this.selectedDriverId = null;
      this.driverSearchText = "";
      if (this.driverSearchTimeout) {
        clearTimeout(this.driverSearchTimeout);
      }
      this.$nextTick(() => {
        this.getDrivers("");
      });
    },

    getDrivers(keyword = null) {
      if (this.driversLoading) return;
      this.driversLoading = true;
      const params = { order_type: "delivery" };
      const searchKey =
        keyword !== null ? keyword : this.driverSearchText || "";
      if (searchKey && typeof searchKey === "string" && searchKey.trim()) {
        params.keyword = searchKey.trim();
      }
      this.$axios
        .get(`order/${this.id}/get_drivers_candidates`, { params })
        .then((res) => {
          this.drivers = (res.data.data || []).map((driver) => {
            const name = driver.name || driver.full_name || "";
            const distanceVal = parseFloat(driver.distance);
            const distanceStr = !isNaN(distanceVal)
              ? ` (${distanceVal.toFixed(2)} كم)`
              : "";
            return {
              ...driver,
              recharge_message:
                driver.recharge_message || driver.wallet_recharge_message,
              notEligible: driver.can_assign === false,
              name: `${name}${distanceStr} - الطلبات النشطة (${driver.active_orders_count})`,
            };
          });
        })
        .catch((err) => {
          console.error(err);
        })
        .finally(() => {
          this.driversLoading = false;
        });
    },
    assignDriver(driver_id) {
      if (!driver_id) return;
      this.isAssigningDriver = true;
      const frmData = new FormData();
      frmData.append("order_type", "delivery");
      frmData.append("driver_id", driver_id);
      this.$axios
        .post(`order/${this.id}/assign_driver`, frmData)
        .then((res) => {
          this.selectedDriverId = null;
          this.driverSearchText = null;
          // API answers { status, data, message }; a false status is a soft failure
          if (res.data.status === false) {
            this.$iziToast.error({
              timeout: 5000,
              message: res.data.message,
              position: "bottomRight",
            });
            return;
          }
          this.setRows();
          this.$iziToast.success({
            timeout: 5000,
            message: res.data.message,
            position: "bottomRight",
          });
        })
        .catch((err) => {
          this.selectedDriverId = null;
          this.$iziToast.error({
            timeout: 5000,
            message: err.response?.data?.message || err.message,
            position: "bottomRight",
          });
        })
        .finally(() => {
          this.isAssigningDriver = false;
        });
    },
    // Silent refetch used by socket/countdown/poll: at most one per 5s, and
    // bursts of triggers collapse into a single trailing request.
    refreshSilently() {
      const MIN_GAP_MS = 5000;
      const wait = MIN_GAP_MS - (Date.now() - (this.lastSilentFetch || 0));
      if (wait > 0) {
        if (!this.silentFetchTimer) {
          this.silentFetchTimer = setTimeout(() => {
            this.silentFetchTimer = null;
            this.refreshSilently();
          }, wait);
        }
        return;
      }
      this.lastSilentFetch = Date.now();
      this.setRows(true);
    },
    // Set Rows
    setRows(silent = false) {
      if (!silent) this.loading = true;
      this.$axios({
        method: "GET",
        url: "/delivery_order/" + this.id,
      })
        .then((res) => {
          this.allData = res.data.data;
          this.live.reset();
          this.loading = false;
          this.feedbackText =
            res.data.data.feedback || res.data.data.admin_feedback || "";

          // Auto select first available log tab
          if (
            this.allData?.wallet_transactions &&
            this.allData.wallet_transactions.length
          ) {
            this.activeLogTab = "wallet";
          } else if (
            this.allData?.order_logs &&
            this.allData.order_logs.length
          ) {
            this.activeLogTab = "logs";
          } else if (this.allData?.call_logs && this.allData.call_logs.length) {
            this.activeLogTab = "calls";
          }

          // Silent refreshes (poll/socket/expiry) must not re-query candidates.
          if (this.canAssignDriver && (!silent || !this.driversRequested)) {
            this.driversRequested = true;
            this.getDrivers();
          }
          this.buildOrderChats();

          if (
            !this.chatsData.length &&
            this.allData?.client?.id &&
            this.allData?.driver?.id
          ) {
            this.getChat();
          }
          if (this.allData?.driver?.lat && this.allData?.driver?.lng) {
            this.marker = {
              lat: parseFloat(this.allData.driver.lat),
              lng: parseFloat(this.allData.driver.lng),
            };
          }
          this.center = {
            lat: this.allData?.end_location?.lat,
            lng: this.allData?.end_location?.lng,
          };
        })
        .catch(() => {
          this.loading = false;
        });
    },
    // Highlight (orange) when the last support message status needs action
    supportChatColor(actor) {
      return actor?.support_chat?.is_last_message_status_take_action
        ? "warning"
        : "primary";
    },
    openSupportChat(userType, actor, userName, userAvatar) {
      if (!actor?.id) return;
      this.supportChat = {
        userType,
        userId: actor.id,
        supportChatId: actor.support_chat?.id || null,
        userName: userName || "",
        userAvatar: userAvatar || "",
      };
    },
    getChat() {
      this.$axios({
        method: "GET",
        url: `order/${this.allData?.chat_order_id}/show_chat?sender_id=${this.allData?.client?.id}&receiver_id=${this.allData?.driver?.id}`,
      }).then((res) => {
        const messages = Array.isArray(res.data.data) ? res.data.data : [];
        this.chatData = res.data.data;

        // Fallback for orders that still come without the `chats` list
        if (!this.chatsData.length && messages.length) {
          const legacyChat = {
            chat_id: this.allData?.chat_order_id,
            order_id: this.allData?.chat_order_id,
            conversation_type: "driver_client",
            sender_id: this.allData?.client?.id,
            receiver_id: this.allData?.driver?.id,
            sender: this.allData?.client,
            receiver: this.allData?.driver,
            messages_count: messages.length,
            has_more_messages: false,
          };

          this.chatsData = [
            {
              ...legacyChat,
              key: "legacy-chat",
              messages: this.sortChatMessages(
                messages.map((message) =>
                  this.normalizeChatMessage(message, legacyChat)
                )
              ),
              current_page: 1,
              last_page: 1,
            },
          ];
          this.activeChatIndex = 0;
        }
      });
    },

    // ========== Order Chats (preview + load more)
    buildOrderChats() {
      const chats = Array.isArray(this.allData?.chats)
        ? this.allData.chats
        : [];
      const typesOrder = [
        "driver_client",
        "driver_store",
        "client_store",
        "order_participants",
      ];

      this.chatsData = chats
        .slice()
        .sort((a, b) => {
          const aIndex = typesOrder.indexOf(a?.conversation_type);
          const bIndex = typesOrder.indexOf(b?.conversation_type);
          return (
            (aIndex === -1 ? typesOrder.length : aIndex) -
            (bIndex === -1 ? typesOrder.length : bIndex)
          );
        })
        .map((chat, index) => ({
          ...chat,
          key: chat.chat_id != null ? `chat-${chat.chat_id}` : `chat-${index}`,
          messages: this.sortChatMessages(
            (chat.messages || []).map((message) =>
              this.normalizeChatMessage(message, chat)
            )
          ),
          current_page: 0,
          last_page: null,
        }));

      this.activeChatIndex = 0;
    },

    loadMoreMessages(chat) {
      if (!chat || this.isLoadingMoreMessages) return;

      const url = this.buildShowChatUrl(chat);
      if (!url) return;

      const chatIndex = this.chatsData.findIndex(
        (item) => item.key === chat.key
      );
      if (chatIndex === -1) return;

      const nextPage = (chat.current_page || 0) + 1;
      this.isLoadingMoreMessages = true;

      this.$axios({
        method: "GET",
        url: `${url}&page=${nextPage}`,
      })
        .then((res) => {
          const incoming = (
            Array.isArray(res.data.data) ? res.data.data : []
          ).map((message) => this.normalizeChatMessage(message, chat));
          const meta = res.data.meta || {};
          const lastPage =
            meta.last_page != null
              ? meta.last_page
              : incoming.length < this.chatMessagesPerPage
              ? nextPage
              : null;

          this.$set(this.chatsData, chatIndex, {
            ...chat,
            messages: this.mergeChatMessages(chat.messages, incoming),
            messages_count:
              meta.total != null ? meta.total : chat.messages_count,
            current_page: nextPage,
            last_page: lastPage,
            has_more_messages:
              lastPage != null
                ? nextPage < lastPage
                : incoming.length >= this.chatMessagesPerPage,
          });
        })
        .catch((err) => {
          this.$iziToast.error({
            timeout: 5000,
            message:
              err.response?.data?.message || "تعذر تحميل المزيد من الرسائل",
            position: "bottomRight",
          });
        })
        .finally(() => {
          this.isLoadingMoreMessages = false;
        });
    },

    buildShowChatUrl(chat) {
      if (!chat) return null;

      const params = chat.show_chat_params || {};
      const senderId =
        params.sender_id != null ? params.sender_id : chat.sender_id;
      const receiverId =
        params.receiver_id != null ? params.receiver_id : chat.receiver_id;
      if (senderId == null || receiverId == null) return null;

      // The axios base url already contains the api prefix
      let path = (chat.show_chat_endpoint || "")
        .split("?")[0]
        .replace(/^https?:\/\/[^/]+/, "")
        .replace(/^\/?api\/[^/]+\//, "")
        .replace(/^\/+/, "");

      if (!path) {
        const orderId =
          chat.order_id != null
            ? chat.order_id
            : this.allData?.chat_order_id || this.id;
        path = `order/${orderId}/show_chat`;
      }

      return `${path}?sender_id=${senderId}&receiver_id=${receiverId}`;
    },

    normalizeChatMessage(message, chat) {
      if (!message) return message;

      const senderId = message.sender_id;
      let senderData = message.sender_data;
      if (!senderData) {
        senderData =
          message.sender && message.sender.id
            ? message.sender
            : (chat && senderId == chat.sender_id
                ? chat.sender
                : chat && chat.receiver) || {};
      }

      let position = message.message_position;
      if (position !== "current" && position !== "other") {
        position = chat && senderId == chat.sender_id ? "current" : "other";
      }

      const normalized = {
        ...message,
        id: message.message_id != null ? message.message_id : message.id,
        sender_data: {
          ...senderData,
          image: (senderData && senderData.image) || imageFalse,
          name: (senderData && (senderData.full_name || senderData.name)) || "",
        },
        message_position: position,
      };

      // The shared chat component plays audio from `sound` and has no file view
      if (normalized.message_type === "sound" && !normalized.sound) {
        normalized.sound = normalized.message;
      }
      if (normalized.message_type === "file" && normalized.message) {
        const fileUrl = String(normalized.message).replace(/"/g, "&quot;");
        normalized.message = `<a href="${fileUrl}" target="_blank" rel="noopener">مرفق: عرض الملف</a>`;
        normalized.message_type = "text";
      }

      return normalized;
    },

    sortChatMessages(messages) {
      const list = Array.isArray(messages) ? messages.slice() : [];
      const allNumeric = list.every(
        (message) => message && !isNaN(parseInt(message.id, 10))
      );
      if (allNumeric) {
        list.sort((a, b) => parseInt(a.id, 10) - parseInt(b.id, 10));
      }
      return list;
    },

    mergeChatMessages(existing, incoming) {
      const map = new Map();
      [...(existing || []), ...(incoming || [])].forEach((message) => {
        if (!message) return;
        const key =
          message.id != null
            ? `id-${message.id}`
            : `time-${message.created_at}-${message.message}`;
        map.set(key, message);
      });
      return this.sortChatMessages([...map.values()]);
    },

    conversationTypeLabel(type) {
      const labels = {
        driver_client: "المندوب والعميل",
        driver_store: "المندوب والمتجر",
        client_store: "العميل والمتجر",
        order_participants: "أطراف الطلب",
      };
      return labels[type] || "محادثة الطلب";
    },

    userTypeLabel(type) {
      const labels = {
        client: "عميل",
        driver: "مندوب",
        store: "متجر",
        resturant: "متجر",
        restaurant: "متجر",
        admin: "الإدارة",
      };
      return labels[type] || type || "-";
    },

    userImage(user) {
      return (user && user.image) || imageFalse;
    },

    callLogActionLabel(type) {
      const labels = {
        call: "مكالمة",
        phone_call: "مكالمة",
        whatsapp: "واتساب",
        whatsapp_call: "مكالمة واتساب",
        whatsapp_message: "رسالة واتساب",
      };
      return labels[type] || type || "-";
    },

    callLogActionColor(type) {
      if (!type) return "grey";
      return String(type).includes("whatsapp") ? "green" : "blue";
    },

    callLogActionIcon(type) {
      if (!type) return "far fa-phone-alt";
      return String(type).includes("whatsapp")
        ? "fab fa-whatsapp"
        : "far fa-phone-alt";
    },
    mapLogic() {
      const socket = io(`${process.env.VUE_APP_BASE_URL_SOCKET}:3073`);
      this.socket = socket;

      let hasConnected = false;
      socket.on("connect", () => {
        // After a reconnect we may have missed updates: reload the order.
        if (hasConnected) this.refreshSilently();
        hasConnected = true;
        if (this.allData?.driver?.id) {
          socket.emit("join_update_location", {
            driver_id: this.allData.driver.id,
            dashboard: true,
          });
        }
      });

      const handleLocationUpdate = (data) => {
        if (!data) return;
        const driverId = data.driver_id;
        const currentDriverId = this.allData?.driver?.id;

        if (
          (data.active_order_id == this.id &&
            data.active_order_type == "delivery") ||
          (currentDriverId && driverId == currentDriverId)
        ) {
          socket.emit("join_update_location", {
            driver_id: driverId,
            dashboard: true,
          });
          this.marker = {
            lat: parseFloat(data.lat),
            lng: parseFloat(data.lng),
          };
        }
      };

      socket.on("update_location", handleLocationUpdate);
      socket.on("update_location_on_map", handleLocationUpdate);
      socket.on("driver_location_update", handleLocationUpdate);

      socket.on(`admin_order_updated:${this.id}`, (data) => {
        if (
          data &&
          data.notify_type === "order_details_updated" &&
          data.order_id == this.id &&
          data.order_type == "delivery"
        ) {
          const statusChanged =
            data.status !== undefined && data.status !== this.allData?.status;
          const complete = this.live.applySnapshot(this, this.allData, data);
          // Incomplete payload or a status change: re-fetch the order.
          if (!complete || statusChanged) this.refreshSilently();
        }
      });
    },
    goToPartnerPage(e) {
      let route = this.$router.resolve({ path: `/driver/show/${e}` });

      window.open(route.href, "_blank");
    },
    onInvoiceFileChange(e) {
      const file = e.target.files[0];
      if (file) {
        this.invoiceFile = file;
        this.invoiceImagePreview = URL.createObjectURL(file);
      }
    },
    clearInvoiceFile() {
      this.invoiceFile = null;
      this.invoiceImagePreview = null;
      if (this.$refs.invoiceFileInput) {
        this.$refs.invoiceFileInput.value = "";
      }
    },
    submitInvoice() {
      if (!this.invoiceFile) {
        this.$iziToast.error({
          message:
            this.lang === "ar"
              ? "صورة الفاتورة مطلوبة"
              : "Invoice image is required",
          position: "bottomRight",
        });
        return;
      }
      this.isSubmittingInvoice = true;

      const formData = new FormData();
      formData.append("invoice_price", this.invoiceCost);
      formData.append("invoice_image", this.invoiceFile);

      this.$axios
        .post(`order/${this.id}/upload-invoice`, formData)
        .then((res) => {
          this.$iziToast.success({
            timeout: 3000,
            message:
              res.data.message ||
              (this.lang === "ar"
                ? "تم رفع الفاتورة بنجاح"
                : "Invoice raised successfully"),
            position: "bottomRight",
          });
          this.invoiceDialog = false;
          this.invoiceCost = "";
          this.invoiceFile = null;
          this.invoiceImagePreview = null;
          this.isSubmittingInvoice = false;
          this.setRows();
        })
        .catch((err) => {
          this.$iziToast.error({
            timeout: 3000,
            message:
              err.response?.data?.message ||
              (this.lang === "ar"
                ? "فشل رفع الفاتورة"
                : "Failed to raise invoice"),
            position: "bottomRight",
          });
          this.isSubmittingInvoice = false;
        });
    },
    getStatusTranslation(status) {
      if (!status) return "";
      const statusMap = {
        pending: "الجديدة",
        restaurant_accept: "مقبول من المتجر",
        restaurant_ready: "جاهز للتوصيل",
        driver_accept: "مقبول من السائق",
        driver_start_order: "بدأ التوصيل",
        driver_pre_finished: "وصول السائق",
        driver_finished_delivery: "إنهاء الطلب",
        client_cancel: "ملغى من العميل",
        restaurant_reject: "مرفوض من المطعم",
        restaurant_cancel: "ملغى من المطعم",
        admin_cancel: "ملغى من الإدارة",
        admin_returned: "مرتجع من الإدارة",
      };
      return statusMap[status] || status;
    },
    getNextStatusLabel(nextStatus) {
      if (!nextStatus) return "";
      if (typeof nextStatus === "object") {
        return nextStatus.label || this.getStatusTranslation(nextStatus.value);
      }
      return this.getStatusTranslation(nextStatus);
    },
    changeOrderStatus() {
      if (!this.allData?.next_available_status) return;

      let nextStatusValue = "";
      if (typeof this.allData.next_available_status === "object") {
        nextStatusValue = this.allData.next_available_status.value;
      } else {
        nextStatusValue = this.allData.next_available_status;
      }

      this.isChangeStatusLoading = true;
      this.$axios
        .put(`order/${this.id}/change-status`, {
          status: nextStatusValue,
        })
        .then((res) => {
          this.isChangeStatusLoading = false;
          this.$iziToast.success({
            timeout: 3000,
            message: res.data.message || "تم تغيير حالة الطلب بنجاح",
            position: "bottomRight",
          });
          this.setRows();
        })
        .catch((err) => {
          this.isChangeStatusLoading = false;
          this.$iziToast.error({
            timeout: 3000,
            message: err.response?.data?.message || "فشل تغيير حالة الطلب",
            position: "bottomRight",
          });
        });
    },
    submitFeedback() {
      this.isSubmittingFeedback = true;
      this.$axios
        .post(`order/${this.id}/feedback`, {
          feedback: this.feedbackText,
        })
        .then((res) => {
          this.isSubmittingFeedback = false;
          this.$iziToast.success({
            timeout: 3000,
            message: res.data.message || "تم حفظ الملاحظات بنجاح",
            position: "bottomRight",
          });
          this.setRows();
        })
        .catch((err) => {
          this.isSubmittingFeedback = false;
          this.$iziToast.error({
            timeout: 3000,
            message: err.response?.data?.message || "فشل حفظ الملاحظات",
            position: "bottomRight",
          });
        });
    },
    submitDriverFine() {
      if (!this.fineReasonObj) {
        this.$iziToast.error({
          message: "السبب مطلوب",
          position: "bottomRight",
        });
        return;
      }
      this.isSubmittingFine = true;
      this.$axios({
        method: "POST",
        url: `order/${this.id}/driver-fines`,
        data: {
          cancel_reason_id: this.fineReasonObj.id,
          reason: this.fineReasonObj.name,
          note: this.fineNote,
        },
      })
        .then((res) => {
          this.$iziToast.success({
            timeout: 2000,
            message: res.data.message || "تم تسجيل المخالفة بنجاح",
            position: "bottomRight",
          });
          this.driverFineDialog = false;
          this.fineReasonObj = null;
          this.fineNote = "";
          this.isSubmittingFine = false;
        })
        .catch((err) => {
          this.$iziToast.error({
            timeout: 2000,
            message: err.response?.data?.message || "فشل تسجيل المخالفة",
            position: "bottomRight",
          });
          this.isSubmittingFine = false;
        });
    },
    getCancelReasons() {
      this.$axios({
        method: "GET",
        url: "cancel_reason_not_paginated?reason_type=fine",
      })
        .then((res) => {
          this.cancelReasons = res.data.data;
        })
        .catch((err) => {
          console.error(err);
        });
    },

    padZero(num) {
      const n = Math.floor(Number(num) || 0);
      return n < 10 ? `0${n}` : `${n}`;
    },

    copyOrderComment() {
      const text = this.allData?.comment ? String(this.allData.comment).trim() : "";
      if (!text) return;

      const triggerCopiedState = () => {
        this.commentCopied = true;
        if (this.commentCopyTimer) clearTimeout(this.commentCopyTimer);
        this.commentCopyTimer = setTimeout(() => {
          this.commentCopied = false;
        }, 2000);
        this.$iziToast.success({
          timeout: 2000,
          message: "تم نسخ تعليق العميل",
          position: "bottomRight",
        });
      };

      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard
          .writeText(text)
          .then(triggerCopiedState)
          .catch(() => {
            const el = document.createElement("textarea");
            el.value = text;
            document.body.appendChild(el);
            el.select();
            document.execCommand("copy");
            document.body.removeChild(el);
            triggerCopiedState();
          });
      } else {
        const el = document.createElement("textarea");
        el.value = text;
        document.body.appendChild(el);
        el.select();
        document.execCommand("copy");
        document.body.removeChild(el);
        triggerCopiedState();
      }
    },

    parseScheduleDate(dateStr, timeStr, periodStr) {
      if (!dateStr && !timeStr) return null;
      try {
        const todayStr = new Date().toISOString().slice(0, 10);
        let datePart = dateStr ? String(dateStr).trim() : todayStr;
        let timePart = timeStr ? String(timeStr).trim() : "00:00";
        let period = periodStr ? String(periodStr).trim().toLowerCase() : "";

        // Normalize Date
        if (/^\d{1,2}[-/]\d{1,2}[-/]\d{4}$/.test(datePart)) {
          const parts = datePart.split(/[-/]/);
          datePart = `${parts[2]}-${parts[1].padStart(
            2,
            "0"
          )}-${parts[0].padStart(2, "0")}`;
        } else if (/^\d{4}[-/]\d{1,2}[-/]\d{1,2}$/.test(datePart)) {
          const parts = datePart.split(/[-/]/);
          datePart = `${parts[0]}-${parts[1].padStart(
            2,
            "0"
          )}-${parts[2].padStart(2, "0")}`;
        }

        // Determine AM / PM from periodStr or from timeStr
        const isPM =
          period === "pm" ||
          period.includes("pm") ||
          period.includes("مساء") ||
          /pm|مساء/i.test(timePart);
        const isAM =
          period === "am" ||
          period.includes("am") ||
          period.includes("صباح") ||
          /am|صباح/i.test(timePart);

        const cleanTime = timePart.replace(/[^\d:]/g, "");
        const timeComponents = cleanTime.split(":");

        let hours = 0;
        let minutes = 0;
        let seconds = 0;

        if (timeComponents.length >= 1) {
          hours = parseInt(timeComponents[0], 10) || 0;
        }
        if (timeComponents.length >= 2) {
          minutes = parseInt(timeComponents[1], 10) || 0;
        }
        if (timeComponents.length >= 3) {
          seconds = parseInt(timeComponents[2], 10) || 0;
        }

        if (isPM && hours < 12) {
          hours += 12;
        } else if (isAM && hours === 12) {
          hours = 0;
        }

        const [y, m, d] = datePart.split("-").map((n) => parseInt(n, 10));
        if (!y || !m || !d) return null;

        const target = new Date(y, m - 1, d, hours, minutes, seconds);
        return isNaN(target.getTime()) ? null : target;
      } catch {
        return null;
      }
    },
  },
  created() {
    // Non-reactive live-update state; recalculate active steps every minute.
    this.live = createLiveTimeline();
    this.tickTimer = setInterval(
      () => this.live.tickOrder(this, this.allData),
      TICK_MS
    );
    this.countdownTimer = setInterval(
      () => {
        // A step just hit zero: re-fetch so the server sets the delay state.
        if (this.live.tickCountdown(this, this.allData)) this.refreshSilently();
      },
      COUNTDOWN_MS
    );
    this.setRows();
    this.mapLogic();
    this.getCancelReasons();
    this.countdownInterval = setInterval(() => {
      this.currentTime = new Date();
    }, 1000);
  },
  beforeDestroy() {
    clearInterval(this.tickTimer);
    clearInterval(this.countdownTimer);
    clearTimeout(this.silentFetchTimer);
    if (this.driverSearchTimeout) {
      clearTimeout(this.driverSearchTimeout);
      this.driverSearchTimeout = null;
    }
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }
    if (this.commentCopyTimer) {
      clearTimeout(this.commentCopyTimer);
      this.commentCopyTimer = null;
    }
    if (this.socket) {
      this.socket.off(`admin_order_updated:${this.id}`);
      this.socket.disconnect();
      this.socket = null;
    }
  },
};
</script>

<style lang="scss" scoped>
.delivery-order-details-wrapper {
  padding: 10px;
  font-family: inherit;

  .dir-ltr {
    direction: ltr !important;
  }

  .icon-flip {
    transform: rotate(90deg);
  }

  /* Scheduled Dispatch Cockpit Widget */
  .scheduled-dispatch-cockpit {
    border-radius: 22px;
    padding: 22px 26px;
    position: relative;
    overflow: hidden;
    backdrop-filter: blur(14px);
    transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);

    /* AM / Morning Golden Sunrise Atmosphere */
    &.period-am {
      background: linear-gradient(
          135deg,
          rgba(255, 247, 237, 0.95) 0%,
          rgba(254, 243, 199, 0.85) 50%,
          rgba(255, 255, 255, 0.95) 100%
        ),
        #ffffff;
      border: 1.5px solid rgba(245, 158, 11, 0.3);
      box-shadow: 0 12px 36px rgba(245, 158, 11, 0.1);

      .period-orb-icon {
        background: linear-gradient(135deg, #f59e0b, #d97706);
        color: #ffffff;
        box-shadow: 0 6px 18px rgba(245, 158, 11, 0.35);
      }

      .cockpit-pipeline-bar {
        background: rgba(245, 158, 11, 0.08);
        border: 1px solid rgba(245, 158, 11, 0.18);
      }
    }

    /* PM / Evening Twilight Starry Atmosphere */
    &.period-pm {
      background: linear-gradient(
          135deg,
          rgba(243, 232, 255, 0.95) 0%,
          rgba(238, 242, 255, 0.9) 50%,
          rgba(255, 255, 255, 0.95) 100%
        ),
        #ffffff;
      border: 1.5px solid rgba(120, 61, 115, 0.25);
      box-shadow: 0 12px 36px rgba(120, 61, 115, 0.08);

      .period-orb-icon {
        background: linear-gradient(135deg, #783d73, #4f46e5);
        color: #ffffff;
        box-shadow: 0 6px 18px rgba(120, 61, 115, 0.35);
      }

      .cockpit-pipeline-bar {
        background: rgba(120, 61, 115, 0.06);
        border: 1px solid rgba(120, 61, 115, 0.15);
      }
    }

    /* Urgency Glowing Pulse Modifiers */
    &.urgency-urgent {
      border-color: rgba(239, 68, 68, 0.5) !important;
      box-shadow: 0 12px 40px rgba(239, 68, 68, 0.18) !important;
      animation: pulse-urgent-card 2s infinite;

      .period-orb-icon {
        background: linear-gradient(135deg, #ef4444, #dc2626) !important;
        box-shadow: 0 6px 20px rgba(239, 68, 68, 0.5) !important;
      }
    }

    &.urgency-due {
      border-color: rgba(16, 185, 129, 0.45) !important;
      box-shadow: 0 12px 36px rgba(16, 185, 129, 0.12) !important;

      .period-orb-icon {
        background: linear-gradient(135deg, #10b981, #059669) !important;
      }
    }

    .period-orb-icon {
      width: 52px;
      height: 52px;
      border-radius: 16px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 24px;
      flex-shrink: 0;
      position: relative;

      .orb-pulse-ring {
        position: absolute;
        inset: -4px;
        border-radius: 20px;
        border: 2px solid currentColor;
        opacity: 0.3;
        animation: spin-pulse 4s linear infinite;
      }
    }

    .cockpit-title {
      font-size: 16.5px;
      color: #1e293b;
      letter-spacing: -0.2px;
    }

    .period-badge-tag {
      font-size: 11.5px;
      font-weight: 700;
      padding: 4px 12px;
      border-radius: 20px;
      display: inline-flex;
      align-items: center;

      &.period-am {
        background: rgba(245, 158, 11, 0.18);
        color: #92400e;
        border: 1px solid rgba(245, 158, 11, 0.35);
      }

      &.period-pm {
        background: rgba(120, 61, 115, 0.15);
        color: #783d73;
        border: 1px solid rgba(120, 61, 115, 0.3);
      }

      .badge-sub-code {
        margin-right: 3px;
        font-weight: 800;
        opacity: 0.85;
      }
    }

    .urgency-pill-tag {
      font-size: 11.5px;
      font-weight: 700;
      padding: 4px 12px;
      border-radius: 20px;
      display: inline-flex;
      align-items: center;
      gap: 6px;

      .pulse-dot {
        width: 7px;
        height: 7px;
        border-radius: 50%;
        background-color: currentColor;
      }

      &.pill-normal {
        background: rgba(59, 130, 246, 0.12);
        color: #1d4ed8;
      }
      &.pill-soon {
        background: rgba(245, 158, 11, 0.18);
        color: #b45309;
      }
      &.pill-urgent {
        background: rgba(239, 68, 68, 0.18);
        color: #b91c1c;
        animation: blink-soft 1.5s infinite;
      }
      &.pill-due {
        background: rgba(16, 185, 129, 0.18);
        color: #047857;
      }
      &.pill-completed {
        background: #e2e8f0;
        color: #475569;
      }
    }

    /* Countdown Pod */
    .cockpit-countdown-pod {
      background: rgba(255, 255, 255, 0.85);
      border: 1px solid rgba(0, 0, 0, 0.08);
      border-radius: 16px;
      padding: 6px 14px;
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.04);

      .pod-sub {
        font-size: 10.5px;
        color: #64748b;
      }
      .pod-main {
        font-size: 12.5px;
        color: #1e293b;
      }

      .pod-digits-group {
        gap: 3px;
      }

      .digit-box {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        background: #ffffff;
        border: 1px solid rgba(0, 0, 0, 0.09);
        border-radius: 10px;
        min-width: 46px;
        padding: 4px 6px;
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.03);

        &.digit-box-sec {
          border-color: rgba(120, 61, 115, 0.3);
          .digit-num {
            color: var(--mainColor-2, #783d73);
          }
        }

        .digit-num {
          font-size: 18px;
          font-weight: 800;
          line-height: 1.1;
          color: #0f172a;
          font-family: inherit;
        }

        .digit-sub {
          font-size: 9.5px;
          color: #64748b;
          font-weight: 600;
          margin-top: 1px;
        }
      }

      .digit-divider {
        font-size: 18px;
        font-weight: 800;
        color: #94a3b8;
        line-height: 1;
        align-self: center;
        margin: 0 1px;
        animation: blink-divider 1.2s infinite;
      }
    }

    .border-top-soft {
      border-top: 1px solid rgba(0, 0, 0, 0.06);
    }

    /* Footer Smart Advice & Chips */
    .smart-advice-pill {
      font-size: 12px;
      font-weight: 600;
      padding: 6px 14px;
      border-radius: 12px;
      background: rgba(255, 255, 255, 0.9);
      border: 1px solid rgba(0, 0, 0, 0.08);
      color: #334155;
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.02);

      &.advice-urgent {
        background: #fef2f2;
        border-color: #fecaca;
        color: #b91c1c;
      }
      &.advice-soon {
        background: #fffbeb;
        border-color: #fde68a;
        color: #b45309;
      }
      &.advice-due {
        background: #ecfdf5;
        border-color: #a7f3d0;
        color: #047857;
      }
    }

    .cockpit-chip {
      background: rgba(255, 255, 255, 0.8);
      border: 1px solid rgba(0, 0, 0, 0.08);
      padding: 4px 10px;
      border-radius: 10px;
      font-size: 11.5px;
      color: #475569;
      font-weight: 600;
    }
  }

  @keyframes spin-pulse {
    0% {
      transform: rotate(0deg) scale(1);
    }
    50% {
      transform: rotate(180deg) scale(1.08);
    }
    100% {
      transform: rotate(360deg) scale(1);
    }
  }

  @keyframes blink-divider {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.25;
    }
  }

  @keyframes blink-soft {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.6;
    }
  }

  @keyframes pulse-urgent-card {
    0%,
    100% {
      box-shadow: 0 12px 36px rgba(239, 68, 68, 0.15);
    }
    50% {
      box-shadow: 0 12px 46px rgba(239, 68, 68, 0.32);
    }
  }

  /* Glassmorphism Hero Bar */
  .order-hero-card {
    background: linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.95),
      rgba(248, 249, 250, 0.9)
    );
    backdrop-filter: blur(10px);
    border: 1px solid rgba(120, 61, 115, 0.15);
    border-radius: 20px;
    padding: 18px 24px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.04);

    .order-id-badge {
      font-size: 16px;
      color: #333;
      .order-num {
        color: var(--mainColor-2, #783d73);
        font-size: 20px;
        font-weight: 800;
      }
    }

    .status-badge-chip {
      background: linear-gradient(135deg, #783d73, #9b4f95);
      color: #fff;
      padding: 6px 16px;
      border-radius: 30px;
      font-size: 13px;
      font-weight: 700;
      display: inline-flex;
      align-items: center;
      gap: 8px;
      box-shadow: 0 4px 12px rgba(120, 61, 115, 0.25);

      .pulse-indicator {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background-color: #26de81;
        box-shadow: 0 0 0 0 rgba(38, 222, 129, 0.7);
        animation: pulse-green 1.8s infinite;
      }
    }

    .created-time-tag {
      color: #777;
      font-size: 13px;
      background: #f0f2f5;
      padding: 6px 14px;
      border-radius: 20px;
    }

    .select-driver-container {
      min-width: 220px;
      max-width: 320px;

      .driver-select-field {
        font-size: 13px;

        .v-input__slot {
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04) !important;
        }
      }
    }

    .custom-hero-btn {
      font-weight: 700;
      letter-spacing: 0;
      text-transform: none;
      box-shadow: 0 4px 14px rgba(0, 0, 0, 0.08);
      transition: all 0.25s ease;

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 6px 18px rgba(0, 0, 0, 0.12);
      }
    }
  }

  @keyframes pulse-green {
    0% {
      transform: scale(0.95);
      box-shadow: 0 0 0 0 rgba(38, 222, 129, 0.7);
    }
    70% {
      transform: scale(1);
      box-shadow: 0 0 0 8px rgba(38, 222, 129, 0);
    }
    100% {
      transform: scale(0.95);
      box-shadow: 0 0 0 0 rgba(38, 222, 129, 0);
    }
  }

  /* Quick Metric KPI Cards */
  .kpi-metric-card {
    background: #ffffff;
    border-radius: 16px;
    padding: 16px;
    display: flex;
    align-items: center;
    gap: 14px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.03);
    border: 1px solid rgba(0, 0, 0, 0.05);
    transition: transform 0.2s ease, box-shadow 0.2s ease;

    &:hover {
      transform: translateY(-3px);
      box-shadow: 0 8px 25px rgba(0, 0, 0, 0.07);
    }

    .kpi-icon {
      width: 48px;
      height: 48px;
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 20px;
      flex-shrink: 0;

      &.icon-purple {
        background: rgba(120, 61, 115, 0.1);
        color: #783d73;
      }
      &.icon-blue {
        background: rgba(33, 150, 243, 0.1);
        color: #2196f3;
      }
      &.icon-green {
        background: rgba(76, 175, 80, 0.1);
        color: #4caf50;
      }
      &.icon-orange {
        background: rgba(255, 152, 0, 0.1);
        color: #ff9800;
      }
    }

    .kpi-info {
      .kpi-label {
        font-size: 12px;
        color: #888;
        display: block;
        margin-bottom: 2px;
      }
      .kpi-value {
        font-size: 14px;
        font-weight: 700;
        color: #2c3e50;

        &.price-text {
          color: var(--mainColor-2, #783d73);
          font-size: 17px;
        }
      }
    }
  }

  /* Horizontal Timeline Stepper */
  .timeline-card-wrapper {
    .horizontal-timeline-scroll {
      overflow-x: auto;
      padding-bottom: 8px;

      &::-webkit-scrollbar {
        height: 6px;
      }
      &::-webkit-scrollbar-thumb {
        background: rgba(120, 61, 115, 0.2);
        border-radius: 10px;
      }
    }

    .horizontal-stepper {
      display: flex;
      align-items: flex-start;
      min-width: 100%;
      padding-top: 10px;
      padding-bottom: 10px;

      .stepper-item {
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;
        flex: 1;
        min-width: 220px;
        padding: 0 10px;
        text-align: center;

        .stepper-line {
          position: absolute;
          top: 20px;
          right: 50%;
          width: 100%;
          height: 3px;
          background: linear-gradient(90deg, #783d73, #e0a3d7);
          z-index: 1;
        }

        .stepper-icon-circle {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: linear-gradient(
            135deg,
            var(--mainColor-2, #783d73),
            #9b4f95
          );
          color: #ffffff;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 14px;
          z-index: 2;
          box-shadow: 0 4px 12px rgba(120, 61, 115, 0.3);
          transition: transform 0.2s ease;

          &:hover {
            transform: scale(1.15);
          }

          &.stepper-icon-circle--danger {
            background: linear-gradient(135deg, #ff2366, #ff6b93);
            box-shadow: 0 4px 12px rgba(255, 35, 102, 0.3);
          }

          &.stepper-icon-circle--success {
            background: linear-gradient(135deg, #16c182, #4fd8a8);
            box-shadow: 0 4px 12px rgba(22, 193, 130, 0.3);
          }

          &.stepper-icon-circle--btn {
            border: none;
            cursor: pointer;
            outline: none;

            &:disabled {
              cursor: default;
              opacity: 0.85;

              &:hover {
                transform: none;
              }
            }
          }
        }

        .stepper-details {
          margin-top: 12px;
          display: flex;
          flex-direction: column;
          align-items: center;

          .stepper-title {
            font-size: 13px;
            font-weight: 700;
            color: #2c3e50;
            margin-bottom: 4px;
            text-align: center;
          }

          .stepper-time {
            font-size: 11px;
            color: #777;
            background: #f0f2f5;
            padding: 2px 10px;
            border-radius: 12px;
            text-align: center;
            max-width: 100%;
            line-height: 1.6;
            white-space: normal;
            word-break: break-word;
          }
        }
      }
    }
  }

  /* Modern Card Container System */
  .modern-card {
    background: #ffffff;
    border-radius: 18px;
    border: 1px solid rgba(0, 0, 0, 0.06);
    box-shadow: 0 4px 18px rgba(0, 0, 0, 0.03);
    overflow: hidden;
    transition: box-shadow 0.25s ease;

    &:hover {
      box-shadow: 0 6px 24px rgba(0, 0, 0, 0.06);
    }

    &.border-left-success {
      border-right: 4px solid #4caf50;
    }
    &.border-left-danger {
      border-right: 4px solid #f44336;
    }
    &.border-left-warning {
      border-right: 4px solid #ff9800;
    }

    .card-header-sm {
      padding: 14px 20px;
      background: #fafbfc;
      border-bottom: 1px solid rgba(0, 0, 0, 0.05);

      .title {
        font-size: 15px;
        font-weight: 800;
        color: var(--mainColor-2, #783d73);
      }

      .view-link {
        font-size: 12px;
        font-weight: 700;
        color: #783d73;
        text-decoration: none;
        transition: opacity 0.2s ease;

        &:hover {
          opacity: 0.8;
        }
      }
    }

    .card-body-sm {
      padding: 20px;
    }
  }

  /* Party Cards (Client, Driver, Store) */
  .party-card {
    .avatar-ring {
      width: 90px;
      height: 90px;
      margin: 0 auto;
      border-radius: 50%;
      padding: 4px;
      background: linear-gradient(135deg, var(--mainColor-2, #783d73), #e0a3d7);
      box-shadow: 0 4px 12px rgba(120, 61, 115, 0.2);

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        border-radius: 50%;
      }
    }

    .store-image-box {
      width: 80px;
      height: 80px;
      margin: 0 auto;
      border-radius: 16px;
      overflow: hidden;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    }

    .card-name {
      color: #2c3e50;
      font-size: 16px;
    }

    .store-location-badge {
      background: #f8f9fb;
      padding: 8px 14px;
      border-radius: 12px;
      border: 1px solid #edf0f5;
      display: inline-block;

      .location-text {
        direction: rtl;
        display: inline-block;
      }
    }

    .badge-light-secondary {
      background: #f1f5f9 !important;
      color: #475569 !important;
      border: 1px solid #e2e8f0 !important;
      padding: 5px 12px;
      border-radius: 12px;
      font-size: 12px;
      font-weight: 600;
      line-height: 1.4;
      display: inline-flex;
      align-items: center;
      gap: 4px;
    }

    .phone-chip {
      display: inline-flex;
      align-items: center;
      background: #f4f6f9;
      color: var(--mainColor-2, #783d73);
      padding: 6px 14px;
      border-radius: 20px;
      font-weight: 700;
      font-size: 13px;
    }
  }

  /* Soft Badges */
  .badge-light-primary {
    background-color: rgba(120, 61, 115, 0.1) !important;
    color: var(--mainColor-2, #783d73) !important;
    border: 1px solid rgba(120, 61, 115, 0.18) !important;
  }
  .badge-light-secondary {
    background-color: #f1f5f9 !important;
    color: #475569 !important;
    border: 1px solid #e2e8f0 !important;
  }
  .badge-light-info {
    background-color: rgba(2, 132, 199, 0.1) !important;
    color: #0284c7 !important;
    border: 1px solid rgba(2, 132, 199, 0.2) !important;
  }
  .badge-light-success {
    background-color: rgba(16, 185, 129, 0.1) !important;
    color: #059669 !important;
    border: 1px solid rgba(16, 185, 129, 0.2) !important;
  }
  .badge-light-warning {
    background-color: rgba(217, 119, 6, 0.1) !important;
    color: #d97706 !important;
    border: 1px solid rgba(217, 119, 6, 0.2) !important;
  }
  .badge-light-danger {
    background-color: rgba(239, 68, 68, 0.1) !important;
    color: #dc2626 !important;
    border: 1px solid rgba(239, 68, 68, 0.2) !important;
  }
  .stepper-time--late {
    background-color: rgba(239, 68, 68, 0.1) !important;
    color: #dc2626 !important;
  }

  /* Spacing Utility Classes */
  .gap-1 {
    gap: 6px;
  }
  .gap-2 {
    gap: 10px;
  }
  .gap-3 {
    gap: 14px;
  }
  .gap-4 {
    gap: 18px;
  }

  /* Receipt Card Styling */
  .receipt-card {
    .payment-type-chip {
      padding: 4px 14px;
      border-radius: 20px;
      font-weight: 700;
      font-size: 13px;
      display: inline-block;

      &.chip-cash {
        background: rgba(76, 175, 80, 0.12);
        color: #2e7d32;
        border: 1px solid rgba(76, 175, 80, 0.25);
      }
      &.chip-online {
        background: rgba(33, 150, 243, 0.12);
        color: #1565c0;
        border: 1px solid rgba(33, 150, 243, 0.25);
      }
    }

    .receipt-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 10px 0;
      font-size: 14px;
      border-bottom: 1px dashed rgba(0, 0, 0, 0.05);

      &:last-child {
        border-bottom: none;
      }

      .label {
        color: #555;
        display: flex;
        align-items: center;
      }
      .value {
        color: #2c3e50;
      }

      .strike-price {
        text-decoration: line-through;
        font-size: 12px;
      }

      &.passive_value {
        opacity: 0.6;
      }
    }

    .coupon-highlight-box {
      background: rgba(120, 61, 115, 0.04);
      border: 1px dashed rgba(120, 61, 115, 0.3);
    }

    .receipt-total-box {
      background: linear-gradient(
        135deg,
        rgba(120, 61, 115, 0.08),
        rgba(120, 61, 115, 0.02)
      );
      border: 1px solid rgba(120, 61, 115, 0.15);
      display: flex;
      justify-content: space-between;
      align-items: center;

      .label {
        font-weight: 800;
        color: #333;
        font-size: 13px;
      }
      .total-amount {
        font-size: 20px;
        font-weight: 900;
        color: var(--mainColor-2, #783d73);
      }
    }
  }

  /* Return Details Card */
  .return-details-card {
    border-right: 4px solid #ff9800 !important;

    .return-detail-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 12px 16px;
      margin-bottom: 10px;
      background: #fafbfc;
      border-radius: 12px;
      border: 1px solid rgba(0, 0, 0, 0.04);

      .return-detail-label {
        color: #555;
        font-size: 14px;
        display: flex;
        align-items: center;
      }
      .return-detail-value {
        color: #2c3e50;
        font-size: 15px;
      }
    }

    .return-total-highlight-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 14px 18px;
      margin-top: 12px;
      background: linear-gradient(
        135deg,
        rgba(244, 67, 54, 0.08),
        rgba(244, 67, 54, 0.02)
      );
      border: 1px solid rgba(244, 67, 54, 0.2);
      border-radius: 14px;

      .return-total-label {
        color: #d32f2f;
        font-size: 15px;
        display: flex;
        align-items: center;
      }
      .return-total-value {
        color: #d32f2f;
        font-size: 18px;
        font-weight: 800;
      }
    }
  }

  /* Document Images */
  .document-img-wrapper {
    overflow: hidden;
    border-radius: 12px;
    border: 1px solid #eee;
    max-height: 220px;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      transition: transform 0.3s ease;

      &:hover {
        transform: scale(1.03);
      }
    }
  }

  /* Modern Data Tables & Tabs Section */
  .tabs-section-card {
    .tabs-header-bar {
      border-top-left-radius: 18px;
      border-top-right-radius: 18px;
    }

    .custom-nav-pills {
      .custom-tab-btn {
        border-radius: 12px !important;
        font-weight: 700;
        font-size: 14px;
        color: #555;
        background: transparent;
        border: 1px solid transparent;
        padding: 10px 16px;
        transition: all 0.25s ease;

        &:hover {
          background: rgba(120, 61, 115, 0.05);
          color: #783d73;
        }

        &.active {
          background: linear-gradient(
            135deg,
            var(--mainColor-2, #783d73),
            #9b4f95
          ) !important;
          color: #ffffff !important;
          box-shadow: 0 4px 14px rgba(120, 61, 115, 0.3);

          .badge-light-primary,
          .badge-light-info,
          .badge-light-warning {
            background-color: rgba(255, 255, 255, 0.25) !important;
            color: #ffffff !important;
          }
        }
      }
    }
  }

  .modern-data-table {
    box-shadow: none !important;

    ::v-deep thead tr {
      background: linear-gradient(
        135deg,
        var(--mainColor-2, #783d73) 0%,
        #9b4f95 100%
      ) !important;
    }

    ::v-deep thead th {
      background-color: transparent !important;
      color: #ffffff !important;
      font-weight: 700 !important;
      font-size: 13px !important;
      border-bottom: none !important;

      span {
        color: #ffffff !important;
        font-weight: 700 !important;
      }

      .v-data-table-header__icon {
        color: #ffffff !important;
      }
    }

    ::v-deep tbody tr {
      transition: background-color 0.2s ease;

      &:hover {
        background-color: rgba(120, 61, 115, 0.02) !important;
      }

      &.wallet-row-driver {
        background-color: rgba(76, 175, 80, 0.14) !important;
        &:hover {
          background-color: rgba(76, 175, 80, 0.22) !important;
        }
      }

      &.wallet-row-restaurant {
        background-color: rgba(255, 152, 0, 0.15) !important;
        &:hover {
          background-color: rgba(255, 152, 0, 0.25) !important;
        }
      }

      &.wallet-row-client {
        background-color: rgba(33, 150, 243, 0.14) !important;
        &:hover {
          background-color: rgba(33, 150, 243, 0.22) !important;
        }
      }
    }

    .index-chip {
      background: #eef0f4;
      color: #555;
      width: 26px;
      height: 26px;
      border-radius: 50%;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      font-size: 12px;
      font-weight: 700;
    }

    .product-item-link {
      display: inline-flex;
      align-items: center;
      gap: 12px;
      text-decoration: none;
      color: #2c3e50;
      font-weight: 700;

      .product-thumb {
        width: 44px;
        height: 44px;
        border-radius: 10px;
        object-fit: cover;
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
      }
    }
  }

  /* Info Grid List */
  .info-grid-list {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
    gap: 16px;

    .info-grid-item {
      background: #f9fafb;
      padding: 12px 16px;
      border-radius: 12px;
      display: flex;
      flex-direction: column;
      gap: 4px;
      border: 1px solid #f0f2f5;

      .info-label {
        font-size: 12px;
        color: #888;
      }
      .info-value {
        font-size: 14px;
        color: #333;
      }
    }
  }

  /* Admin Notes Textarea */
  .custom-admin-textarea {
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 12px;
    padding: 14px;
    font-size: 14px;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;

    &:focus {
      border-color: #783d73;
      box-shadow: 0 0 0 3px rgba(120, 61, 115, 0.15);
      outline: none;
    }
  }

  /* Customer Order Comment Showcase Card */
  .order-comment-showcase-card {
    position: relative;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

    &.has-comment {
      border: 1.5px solid rgba(120, 61, 115, 0.22);
      border-right: 5px solid var(--mainColor-2, #783d73);
      background: linear-gradient(
        135deg,
        #ffffff 0%,
        rgba(250, 245, 255, 0.7) 55%,
        rgba(255, 251, 235, 0.55) 100%
      );
      box-shadow: 0 8px 26px rgba(120, 61, 115, 0.07);

      &:hover {
        box-shadow: 0 12px 32px rgba(120, 61, 115, 0.12);
        transform: translateY(-2px);
      }

      .card-header-sm {
        background: rgba(255, 255, 255, 0.75);
        backdrop-filter: blur(8px);
        border-bottom: 1px dashed rgba(120, 61, 115, 0.15);
      }
    }

    &.is-empty-comment {
      border: 1px dashed rgba(148, 163, 184, 0.45);
      background: #fcfcfd;
    }

    .comment-orb-icon {
      width: 42px;
      height: 42px;
      border-radius: 13px;
      background: linear-gradient(
        135deg,
        var(--mainColor-2, #783d73),
        #9b4f95
      );
      color: #ffffff;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 18px;
      position: relative;
      flex-shrink: 0;
      box-shadow: 0 4px 12px rgba(120, 61, 115, 0.28);

      .orb-ring {
        position: absolute;
        inset: -3px;
        border-radius: 15px;
        border: 1.5px solid rgba(120, 61, 115, 0.35);
        animation: spin-pulse 4s linear infinite;
      }
    }

    &.is-empty-comment .comment-orb-icon {
      background: #f1f5f9;
      color: #64748b;
      box-shadow: none;
    }

    .comment-subtitle {
      font-size: 11.5px;
      display: block;
      margin-top: 1px;
    }

    .comment-status-pill {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 4px 12px;
      border-radius: 20px;
      font-size: 11.5px;
      font-weight: 700;

      &.pill-active {
        background: rgba(120, 61, 115, 0.12);
        color: var(--mainColor-2, #783d73);
        border: 1px solid rgba(120, 61, 115, 0.22);

        .status-dot {
          width: 7px;
          height: 7px;
          border-radius: 50%;
          background: #f59e0b;
          box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.22);
        }
      }

      &.pill-muted {
        background: #f1f5f9;
        color: #64748b;
        border: 1px solid #e2e8f0;
      }
    }

    .copy-comment-btn {
      border: 1px solid rgba(120, 61, 115, 0.22);
      background: #ffffff;
      color: var(--mainColor-2, #783d73);
      border-radius: 20px;
      padding: 4px 12px;
      font-size: 12px;
      font-weight: 700;
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      transition: all 0.2s ease;

      &:hover {
        background: var(--mainColor-2, #783d73);
        color: #ffffff;
        box-shadow: 0 4px 10px rgba(120, 61, 115, 0.22);
      }

      &.copied {
        background: #10b981;
        border-color: #10b981;
        color: #ffffff;
      }
    }

    .comment-quote-stage {
      position: relative;
      background: #ffffff;
      border: 1px solid rgba(120, 61, 115, 0.14);
      border-radius: 16px;
      padding: 18px 20px;
      overflow: hidden;
      box-shadow: inset 0 2px 6px rgba(120, 61, 115, 0.03),
        0 4px 14px rgba(0, 0, 0, 0.02);

      .quote-watermark {
        position: absolute;
        top: 10px;
        left: 14px;
        font-size: 46px;
        color: rgba(120, 61, 115, 0.06);
        pointer-events: none;
        line-height: 1;
      }

      .comment-content-text {
        position: relative;
        z-index: 1;
        font-size: 15px;
        font-weight: 600;
        color: #1e293b;
        line-height: 1.75;
        white-space: pre-wrap;
        word-break: break-word;
        padding-right: 12px;
        border-right: 3px solid #f59e0b;
      }

      .comment-author-bar {
        position: relative;
        z-index: 1;
        border-top: 1px dashed rgba(120, 61, 115, 0.15);

        .author-mini-avatar {
          width: 34px;
          height: 34px;
          border-radius: 50%;
          object-fit: cover;
          border: 2px solid rgba(120, 61, 115, 0.25);
        }

        .author-name {
          font-size: 13px;
          color: #1e293b;
          line-height: 1.2;
        }

        .author-role {
          font-size: 11px;
        }

        .comment-time-chip {
          font-size: 11.5px;
          color: #64748b;
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          padding: 3px 10px;
          border-radius: 12px;
          font-weight: 600;
        }
      }
    }

    .comment-empty-box {
      padding: 6px 4px;

      .empty-comment-icon {
        width: 40px;
        height: 40px;
        border-radius: 12px;
        background: #f1f5f9;
        color: #94a3b8;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 17px;
        flex-shrink: 0;
      }

      .extra-small {
        font-size: 12px;
      }
    }
  }

  /* Dialog Enhancements */
  .modern-dialog-card {
    border-radius: 24px !important;

    .custom-input-field,
    .custom-textarea-field {
      border-radius: 12px;
      border: 1px solid rgba(0, 0, 0, 0.15);
      padding: 12px 14px;
      font-size: 14px;

      &:focus {
        border-color: #783d73;
        box-shadow: 0 0 0 3px rgba(120, 61, 115, 0.15);
      }
    }
  }

  /* Custom Dropzone File Upload */
  .custom-file-upload-box {
    border: 2px dashed rgba(120, 61, 115, 0.3);
    background: #faf7fa;
    border-radius: 16px;
    transition: all 0.2s ease;

    &:hover {
      border-color: #783d73;
      background: #f5eff5;
    }

    .upload-placeholder {
      padding: 24px;
    }

    .remove-upload-btn {
      position: absolute;
      top: 10px;
      right: 10px;
      background: rgba(244, 67, 54, 0.9);
      color: #fff;
      border: none;
      width: 32px;
      height: 32px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
    }
  }

  /* Call Logs Message Body */
  .call-log-message {
    max-width: 320px;
    margin: 0 auto;
    white-space: pre-wrap;
    word-break: break-word;
    font-size: 13px;
    line-height: 1.6;
    text-align: right;
    background: rgba(120, 61, 115, 0.05);
    border-radius: 10px;
    padding: 6px 10px;
  }

  /* Chat Section Card & Empty State */
  .chat-section-card {
    .chat-tabs-bar {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
    }

    .chat-tab-btn {
      border: 1px solid rgba(120, 61, 115, 0.18);
      background: #fafbfc;
      color: #5a5a5a;
      border-radius: 40px;
      padding: 7px 16px;
      font-size: 13px;
      font-weight: 700;
      transition: all 0.25s ease;

      &:hover {
        background: rgba(120, 61, 115, 0.08);
      }

      &.active {
        background: var(--mainColor-2, #783d73);
        border-color: var(--mainColor-2, #783d73);
        color: #fff;

        .badge {
          background: rgba(255, 255, 255, 0.25);
          color: #fff;
        }
      }
    }

    .chat-pane-header {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 12px;
      padding: 10px 14px;
      border-radius: 14px;
      background: linear-gradient(135deg, #fafbfc, #f4f6f9);
      border: 1px solid rgba(120, 61, 115, 0.12);

      .chat-participant {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 13px;
      }

      .chat-participant-img {
        width: 38px;
        height: 38px;
        border-radius: 50%;
        object-fit: cover;
        border: 2px solid #fff;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
      }

      .chat-participant-sep {
        color: var(--mainColor-2, #783d73);
        opacity: 0.6;
      }

      .chat-type-chip {
        margin-right: auto;
        background: rgba(120, 61, 115, 0.08);
        color: var(--mainColor-2, #783d73);
        border-radius: 40px;
        padding: 4px 14px;
        font-size: 12px;
        font-weight: 700;
      }
    }
    .chat-empty-state {
      background: linear-gradient(135deg, #fafbfc, #f4f6f9);
      border-radius: 16px;
      border: 1px dashed rgba(120, 61, 115, 0.18);

      .empty-icon-ring {
        width: 70px;
        height: 70px;
        margin: 0 auto;
        border-radius: 50%;
        background: rgba(120, 61, 115, 0.08);
        color: var(--mainColor-2, #783d73);
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 4px 14px rgba(120, 61, 115, 0.1);
      }

      .max-w-400 {
        max-width: 400px;
      }
    }
  }

  /* Responsive Enhancements */
  @media (max-width: 991px) {
    .order-hero-card {
      padding: 16px;
    }

    .kpi-metric-card {
      padding: 14px;
      .kpi-icon {
        width: 42px;
        height: 42px;
        font-size: 18px;
      }
      .kpi-info .kpi-value {
        font-size: 13px;
        &.price-text {
          font-size: 15px;
        }
      }
    }
  }

  @media (max-width: 768px) {
    .delivery-order-details-wrapper {
      padding: 4px;
    }

    .order-hero-card {
      border-radius: 16px;

      .order-id-badge {
        font-size: 14px;
        .order-num {
          font-size: 17px;
        }
      }

      .status-badge-chip {
        padding: 4px 12px;
        font-size: 12px;
      }

      .select-driver-container {
        width: 100%;
        min-width: 100%;
        max-width: 100%;

        .driver-select-field {
          width: 100%;
          min-width: 100%;
          max-width: 100%;
        }
      }

      .custom-hero-btn {
        width: 100%;
        justify-content: center;
        margin-bottom: 4px;
      }
    }

    /* Vertical Timeline Stepper on Mobile Devices */
    .timeline-card-wrapper {
      .horizontal-timeline-scroll {
        overflow-x: visible;
      }

      .horizontal-stepper {
        flex-direction: column;
        align-items: flex-start;
        padding-right: 15px;

        .stepper-item {
          flex-direction: row;
          align-items: flex-start;
          width: 100%;
          min-width: unset;
          padding: 0 0 24px 0;
          text-align: right;

          &:last-child {
            padding-bottom: 0;
          }

          .stepper-line {
            top: 40px;
            bottom: 0;
            right: 19px;
            left: auto;
            width: 3px;
            height: calc(100% - 16px);
            background: linear-gradient(180deg, #783d73, #e0a3d7);
          }

          .stepper-icon-circle {
            width: 38px;
            height: 38px;
            font-size: 13px;
            flex-shrink: 0;
          }

          .stepper-details {
            margin-top: 0;
            margin-right: 16px;
            align-items: flex-start;

            .stepper-title {
              text-align: right;
              font-size: 14px;
            }

            .stepper-time {
              text-align: right;
            }
          }
        }
      }
    }

    .modern-card {
      border-radius: 14px;

      .card-header-sm {
        padding: 12px 16px;
        .title {
          font-size: 14px;
        }
      }

      .card-body-sm {
        padding: 14px;
      }
    }

    .tabs-section-card {
      .custom-nav-pills {
        flex-direction: column;
        .custom-tab-btn {
          width: 100%;
          text-align: center;
        }
      }
    }

    .info-grid-list {
      grid-template-columns: 1fr;
    }
  }

  @media (max-width: 576px) {
    .kpi-metric-card {
      margin-bottom: 10px;
    }

    .map-card GmapMap {
      height: 350px !important;
    }

    .receipt-card {
      .receipt-row {
        font-size: 13px;
      }

      .receipt-total-box {
        padding: 12px;
        .label {
          font-size: 12px;
        }
        .total-amount {
          font-size: 17px;
        }
      }
    }
  }

  /* Settled Badge in Header */
  .settled-badge-chip {
    display: inline-flex;
    align-items: center;
    padding: 6px 14px;
    background: rgba(16, 185, 129, 0.12);
    border: 1px solid rgba(16, 185, 129, 0.3);
    border-radius: 20px;
    color: #065f46;
    font-size: 13px;
    font-weight: 700;

    .settlement-amount-tag {
      color: #047857;
      font-weight: 800;
    }
  }

  /* Settlement Financial Summary Box in Receipt Card */
  .settlement-financial-box {
    background: #f0fdf4;
    border: 1px solid #bbf7d0;

    .receipt-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
    }
  }
}
</style>

<style lang="scss">
/* Ultra Creative Settle and Finish Modal Styling (Unscoped for Vuetify Teleported Dialog) */
.settle-finish-super-modal {
  border-radius: 28px !important;
  max-height: 90vh !important;
  overflow: hidden !important;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25),
    0 0 0 1px rgba(0, 0, 0, 0.05) !important;

  &.v-dialog--active {
    display: flex !important;
    flex-direction: column !important;
  }

  &:not(.v-dialog--active) {
    display: none !important;
  }

  .settle-creative-card {
    border-radius: 28px !important;
    background: #ffffff;
    border: none;
    max-height: 90vh !important;
    height: 100%;
    display: flex !important;
    flex-direction: column !important;
    overflow: hidden !important;
    position: relative;

    &.v-card {
      padding: 0 !important;
    }

    .modal-glow-top-bar {
      height: 6px;
      width: 100%;
      flex-shrink: 0;
      background: linear-gradient(90deg, #0d9488 0%, #10b981 50%, #6366f1 100%);
    }

    .modal-floating-close-btn {
      position: absolute;
      top: 18px;
      left: 18px;
      width: 36px;
      height: 36px;
      border-radius: 50%;
      border: 1px solid #e2e8f0;
      background: #f8fafc;
      color: #64748b;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      transition: all 0.2s ease;
      z-index: 15;

      &:hover:not(:disabled) {
        background: #fee2e2;
        color: #ef4444;
        border-color: #fecaca;
        transform: rotate(90deg);
      }

      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    }

    .settle-dialog-scroll-body {
      flex: 1 1 auto;
      overflow-y: auto !important;
      overscroll-behavior: contain;
      padding: 24px 32px 16px !important;

      &::-webkit-scrollbar {
        width: 6px;
      }
      &::-webkit-scrollbar-track {
        background: #f8fafc;
      }
      &::-webkit-scrollbar-thumb {
        background: #cbd5e1;
        border-radius: 4px;
      }
      &::-webkit-scrollbar-thumb:hover {
        background: #94a3b8;
      }
    }

    .settle-dialog-sticky-footer {
      flex-shrink: 0;
      background: #ffffff;
      border-top: 1px solid #f1f5f9;
      box-shadow: 0 -4px 16px rgba(0, 0, 0, 0.04);
      z-index: 10;
      padding: 16px 32px !important;
    }

    /* Hero Badge & Icon */
    .settle-hero-badge-wrap {
      position: relative;
      width: 72px;
      height: 72px;

      .settle-hero-pulse-ring {
        position: absolute;
        inset: -6px;
        border-radius: 50%;
        background: rgba(13, 148, 136, 0.18);
        animation: pulseSettle 2.4s infinite ease-out;
      }

      .settle-hero-icon-box {
        position: relative;
        width: 100%;
        height: 100%;
        border-radius: 50%;
        background: linear-gradient(135deg, #0d9488 0%, #10b981 100%);
        display: flex;
        align-items: center;
        justify-content: center;
        color: #ffffff;
        font-size: 30px;
        box-shadow: 0 10px 25px -4px rgba(13, 148, 136, 0.45);
      }
    }

    .settle-tag-pill {
      display: inline-flex;
      align-items: center;
      padding: 4px 14px;
      border-radius: 20px;
      background: rgba(13, 148, 136, 0.1);
      color: #0d9488;
      font-size: 12px;
      font-weight: 700;
      letter-spacing: 0.2px;
    }

    .settle-modal-title {
      font-size: 22px;
      color: #0f172a;
      letter-spacing: -0.3px;
    }

    .settle-modal-desc {
      font-size: 13px;
      color: #64748b;
      line-height: 1.6;
      max-width: 440px;
      margin: 0 auto;
    }

    /* Cockpit Hero Card */
    .settle-hero-cockpit-card {
      background: linear-gradient(
        135deg,
        #f0fdfa 0%,
        #ecfdf5 45%,
        #f8fafc 100%
      );
      border: 1.5px solid rgba(13, 148, 136, 0.22);
      border-radius: 20px;
      box-shadow: 0 10px 30px -10px rgba(13, 148, 136, 0.15);

      .cockpit-top {
        .cockpit-label {
          font-size: 13px;
          font-weight: 700;
          color: #334155;
        }

        .cockpit-badge {
          font-size: 11px;
          font-weight: 700;
          padding: 3px 10px;
          border-radius: 12px;
          background: rgba(16, 185, 129, 0.15);
          color: #065f46;
        }
      }

      .cockpit-amount-row {
        .cockpit-amount-val {
          font-size: 38px;
          color: #0f172a;
          line-height: 1;
          letter-spacing: -1px;
        }

        .cockpit-currency {
          font-size: 16px;
          color: #0d9488;
        }
      }

      .border-top-soft {
        border-top: 1px dashed rgba(13, 148, 136, 0.2);
      }

      /* Flow Pipeline */
      .settle-flow-track {
        .flow-party {
          .party-mini-icon {
            width: 36px;
            height: 36px;
            border-radius: 10px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 15px;

            &.icon-deduct {
              background: #fee2e2;
              color: #ef4444;
            }

            &.icon-receive {
              background: #d1fae5;
              color: #10b981;
            }
          }

          .party-name {
            font-size: 13px;
            color: #1e293b;
          }

          .party-sub-status {
            font-size: 11px;
          }
        }

        .flow-direction-connector {
          position: relative;

          .flow-animated-line {
            height: 2px;
            background: linear-gradient(90deg, #10b981, #0d9488, #ef4444);
            position: relative;
            margin-bottom: 6px;

            .flow-dot-pulse {
              position: absolute;
              width: 8px;
              height: 8px;
              border-radius: 50%;
              background: #0d9488;
              top: -3px;
              right: 20%;
              animation: movePulse 2s infinite ease-in-out;
            }

            .flow-arrow-icon {
              position: absolute;
              top: -8px;
              left: 45%;
              font-size: 14px;
              color: #0d9488;
              background: #ecfdf5;
              padding: 0 4px;
            }
          }

          .flow-transfer-tag {
            font-size: 11px;
            font-weight: 700;
            color: #0d9488;
          }
        }
      }
    }

    /* Breakdown Lines List */
    .settlement-lines-container {
      .lines-title {
        letter-spacing: 0.2px;
      }

      .badge-operations-counter {
        padding: 3px 10px;
        border-radius: 12px;
        background: #f1f5f9;
        color: #475569;
        font-weight: 700;
      }

      .settle-line-card {
        background: #ffffff;
        border-radius: 16px;
        box-shadow: 0 2px 10px rgba(0, 0, 0, 0.04);
        transition: all 0.25s ease;

        &:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(0, 0, 0, 0.07);
        }

        &.line-withdrawal {
          border: 1.5px solid #fee2e2;
          border-right: 5px solid #ef4444;
          background: linear-gradient(135deg, #ffffff 0%, #fffbfb 100%);
        }

        &.line-deposit {
          border: 1.5px solid #d1fae5;
          border-right: 5px solid #10b981;
          background: linear-gradient(135deg, #ffffff 0%, #f7fefb 100%);
        }

        .line-side-avatar {
          width: 44px;
          height: 44px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 18px;

          &.avatar-deduct {
            background: rgba(239, 68, 68, 0.12);
            color: #dc2626;
          }

          &.avatar-credit {
            background: rgba(16, 185, 129, 0.12);
            color: #059669;
          }
        }

        .line-user-title {
          font-size: 14px;
        }

        .line-pill-badge {
          font-size: 11px;
          font-weight: 700;
          padding: 2px 10px;
          border-radius: 12px;

          &.pill-deduct {
            background: rgba(239, 68, 68, 0.1);
            color: #b91c1c;
          }

          &.pill-credit {
            background: rgba(16, 185, 129, 0.1);
            color: #047857;
          }
        }

        .line-meta-desc {
          font-size: 12px;
        }

        /* Amount Pill */
        .line-amount-badge {
          padding: 6px 14px;
          border-radius: 14px;
          display: inline-flex;
          align-items: center;
          gap: 2px;
          font-size: 16px;
          font-weight: 800;
          direction: ltr;

          &.amount-negative {
            background: #fef2f2;
            color: #dc2626;
            border: 1px solid #fecaca;
          }

          &.amount-positive {
            background: #f0fdf4;
            color: #059669;
            border: 1px solid #bbf7d0;
          }

          .amount-curr {
            font-size: 11px;
            font-weight: 700;
            margin-left: 2px;
          }
        }
      }
    }

    /* Safety Guarantee Banner */
    .settle-safety-note {
      background: #f8fafc;
      border: 1px solid #e2e8f0;

      .safety-icon-ring {
        width: 32px;
        height: 32px;
        min-width: 32px;
        border-radius: 50%;
        background: rgba(13, 148, 136, 0.1);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 14px;
      }

      .safety-text {
        line-height: 1.5;
        color: #64748b;
      }
    }

    /* Settle Empty Alert */
    .settle-empty-alert {
      background: #f0fdfa;
      border: 1px dashed #99f6e4;
      color: #0f766e;
      font-size: 13px;
    }

    /* Action Buttons */
    .settle-action-confirm-btn {
      background: linear-gradient(135deg, #0d9488 0%, #059669 100%) !important;
      box-shadow: 0 10px 24px -5px rgba(13, 148, 136, 0.45) !important;
      font-size: 15px !important;
      letter-spacing: 0 !important;
      transition: all 0.25s ease !important;

      &:hover:not(:disabled) {
        transform: translateY(-2px);
        box-shadow: 0 14px 28px -4px rgba(13, 148, 136, 0.55) !important;
      }
    }

    .settle-action-cancel-btn {
      background: #f1f5f9 !important;
      color: #64748b !important;
      font-size: 14px !important;
      transition: all 0.2s ease !important;

      &:hover:not(:disabled) {
        background: #e2e8f0 !important;
        color: #1e293b !important;
      }
    }
  }
}

@keyframes pulseSettle {
  0% {
    transform: scale(0.95);
    opacity: 0.8;
  }
  50% {
    transform: scale(1.15);
    opacity: 0.25;
  }
  100% {
    transform: scale(0.95);
    opacity: 0.8;
  }
}

@keyframes movePulse {
  0% {
    right: 20%;
  }
  50% {
    right: 75%;
  }
  100% {
    right: 20%;
  }
}
</style>
